// src/InvoicePage.jsx

import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { db, storage } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  runTransaction,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { FRAME_COLORS } from "./utils/frameOptions";
import { syncProposalWithInvoice } from "./proposals/fromInvoice";
import { COPYRIGHT_TEXT, COPYRIGHT_TITLE, copyrightLineEn, copyrightYear } from "./utils/copyrightNotice";
import {
  WALLPAPER,
  DEFAULT_WALLPAPER_RATES,
  isWallpaper,
  wallpaperInstallLabel,
  wallpaperRateFor,
  wallpaperAreaM2,
  wallpaperRatesFromPricing,
} from "./utils/wallpaper";
import {
  createProjectItem,
  computeProjectResults,
  buildInvoiceItemsFromProjectRows,
  toNumber,
  getProjectInvoiceLabel,
} from "./utils/projectCalc";

// 📐 فاتورة مشروع (بوكس "فاتورة مشروع" بنموذج الإنشاء) - محصورة ببنين والمدير
// (نفس PROJECT_INVOICE_USERS بـinvoiceController.js)
const PROJECT_INVOICE_USERS = ["admin", "Baneen@123"];

// سعر البيع الظاهر بجدول فاتورة المشروع = سعر المشروع (سعر البيع × المعامل).
// مشروع الموظفة معامله 1 (نفس سعر البيع اللي تكتبه)، ومشروع المدير يحتفظ
// بمعامله - فالموظفة تشوف السعر النهائي للزبون بدون سعر اللوحة/المعامل.
const getProjectRowFactor = (it) => toNumber(it.customValue) || 1;

// يطبّق بنود المشروع على الفاتورة: يحسب الصفوف ← بنود فاتورة عادية ←
// المجموع الأولي ← recalcInvoice (نفس مسار إضافة اللوحات اليدوي)
const applyProjectItemsToInvoice = (inv, projectItems) => {
  const results = computeProjectResults(projectItems);
  const items = buildInvoiceItemsFromProjectRows(results.rows);
  const subtotal = items.reduce((sum, it) => sum + Number(it.total || 0), 0);
  return recalcInvoice({ ...inv, projectItems, items, subtotal });
};

/* =============================
   1) PRICE MAP (from Excel file)
   ============================= */

const PRICE_MAP = {
  L: {
    Origin: {
      "30x40": 45000,
      "40x60": 70000,
      "50x70": 85000,
      "60x80": 100000,
      "70x100": 125000,
      "80x120": 185000,
      "120x180": 295000,
      "160x240": 395000,
      "30x30": 40000,
      "40x40": 45000,
      "50x50": 60000,
      "60x60": 65000,
      "70x70": 95000,
      "80x80": 100000,
      "90x90": 115000,
      "100x100": 135000,
      "110x110": 175000,
      "120x120": 185000,
      "130x130": 215000,
    },
    Edition: {
      "30x40": 75000,
      "40x60": 115000,
      "50x70": 135000,
      "60x80": 165000,
      "70x100": 195000,
      "80x120": 275000,
      "120x180": 395000,
      "160x240": 515000,
      "30x30": 75000,
      "40x40": 85000,
      "50x50": 100000,
      "60x60": 115000,
      "70x70": 150000,
      "80x80": 165000,
      "90x90": 185000,
      "100x100": 215000,
      "110x110": 265000,
      "120x120": 275000,
      "130x130": 315000,
    },
  },
  Kas: {
    Origin: {
      "30x40": 65000,
      "40x60": 75000,
      "50x70": 105000,
      "60x80": 140000,
      "70x100": 175000,
      "80x120": 245000,
      "120x180": 385000,
      "160x240": 495000,
    },
    Edition: {
      "30x40": 95000,
      "40x60": 115000,
      "50x70": 165000,
      "60x80": 205000,
      "70x100": 245000,
      "80x120": 325000,
      "120x180": 495000,
      "160x240": 615000,
    },
  },
};

// 🧻 ورق جدران: بدون إطار + مع تركيب أو بدون، سعره بالمتر (utils/wallpaper.js)
const PANEL_TYPES = ["أوريجن", "إدشن", "برايم", WALLPAPER];

// ✅ التسمية الظاهرة للزبون بالفاتورة فقط (لا تغيّر منطق التسعير أو القيمة المخزنة)
// هذه القيم هي الافتراضية فقط - الموظفة تكدر تعدلهن لكل فاتورة من حقول
// "تسميات نوع اللوحة" بنموذج الفاتورة (تنحفظ داخل invoice.panelTypeLabels)
const PANEL_TYPE_INVOICE_LABELS = {
  "أوريجن": "طباعة فقط",
  "إدشن": "طباعة -رسم بنسبة من 35% إلى 50%",
  "برايم": "طباعة - رسم بنسبة من 85% إلى 95%",
  [WALLPAPER]: "ورق جدران",
};
// customLabels: invoice.panelTypeLabels (تعديل الموظفة) - إذا فاضي أو ماكو تعديل
// لهذا النوع نرجع للتسمية الافتراضية
const resolvePanelTypeLabel = (panelType, customLabels) =>
  (customLabels && customLabels[panelType]) ||
  PANEL_TYPE_INVOICE_LABELS[panelType] ||
  panelType;

// 📐 بند عرض السعر لفاتورة المشروع (src/proposals/fromInvoice.js) - نفس نصوص
// أعمدة جدول الفاتورة بالمعاينة (الوصف/الإطار/الأبعاد/العدد/سعر اللوحة)
const buildProposalPriceItem = (item, panelTypeLabels) => ({
  description: [item.panelName, resolvePanelTypeLabel(item.panelType, panelTypeLabels)]
    .filter(Boolean)
    .join(" – "),
  frame: isWallpaper(item)
    ? wallpaperInstallLabel(item)
    : item.frameOption === "إطار كامل" && item.frameType && item.frameColor
      ? `${item.frameType === "أخرى" ? item.customFrameType : item.frameType} - ${
          item.frameColor === "أخرى" ? item.customFrameColor : item.frameColor
        }`
      : item.frameOption || "",
  dimensions: String(item.sizeKey || "").replace("x", "×"),
  quantity: Number(item.quantity || 0),
  unitPrice: Number(item.pricePerPanel || 0),
});
const PANEL_SHAPES = [
  "لوحة مربعة",
  "لوحة مستطيلة أفقية",
  "لوحة مستطيلة عمودية",
];
const FRAME_OPTIONS = ["بدون إطار", "إطار داخلي فقط", "إطار كامل"];

const FRAME_TYPES = [
  { value: "L", label: "L Frame" },
  { value: "Kas", label: "Kas Frame" },
  { value: "أخرى", label: "أخرى" },
];
// ألوان الإطار صارت بملف مشترك (utils/frameOptions.js) - نفس القيم حرفياً،
// تُستخدم أيضاً بفورم "طلب جديد" مال ارت تاتش

const FRAME_IMAGES = {
   L: {
    "اطار-ال-شامبان-خشن": "/frames/L/brown-rough.png",
    "اطار-ال-قهوائي-خشن": "/frames/L/champagne-rough.png",
    "اطار-ال-ناعم-شامباني": "/frames/L/champagne-smooth.png",
    "اطار-ال-ناعم-فضي": "/frames/L/silver-smooth.png",
  },
 
  Kas: {
    "اطار-كاس-اسود-خشن":  "/frames/Kas/black-rough.png",
    "اطار-كاس-خشن-فاتح": "/frames/Kas/light-wood-rough.png",
    "اطار-كاس-خشن-ذهبي": "/frames/Kas/gold-rough.png",
    "اطار-كاس-خشن-شامباني": "/frames/Kas/champagne-rough.png",
    "اطار-كاس-خشن-فضي": "/frames/Kas/silver-rough.png",
    "اطار-كاس-خشن-قهوائي-طوخ": "/frames/Kas/light-brown-rough.png",
    "اطار-كاس-خشن-قهوائي-فاتح": "/frames/Kas/dark-brown-rough.png",
    "اطار-كاس-ناعم-ذهبي-مشطب": "/frames/Kas/gold-smooth-brushed.png",
    "اطار-كاس-ناعم-شامباني": "/frames/Kas/champagne-smooth.png",
  },
};
const SIZE_OPTIONS = [
  "30x30",
  "30x40",
  "40x40",
  "40x60",
  "50x50",
  "50x70",
  "60x60",
  "60x80",
  "70x70",
  "70x100",
  "80x80",
  "80x120",
  "90x90",
  "100x100",
  "110x110",
  "120x120",
  "120x180",
  "130x130",
  "160x240",
];

const toPriceTypeKey = (panelType) => {
  if (panelType === "أوريجن") return "Origin";
  if (panelType === "إدشن") return "Edition";
  return null;
};
const getSizeParts = (size) => {
  return size.split("x").map(Number);
};

const getFilteredSizeOptions = (panelShape) => {
  if (panelShape === "لوحة مربعة") {
    return SIZE_OPTIONS.filter((size) => {
      const [w, h] = getSizeParts(size);
      return w === h;
    });
  }

  if (
    panelShape === "لوحة مستطيلة أفقية" ||
    panelShape === "لوحة مستطيلة عمودية"
  ) {
    return SIZE_OPTIONS.filter((size) => {
      const [w, h] = getSizeParts(size);
      return w !== h;
    });
  }

  return SIZE_OPTIONS;
};

const ceilToStep = (value, step) => Math.ceil(value / step) * step;

const calculateCustomPanelPrice = (item) => {
  const height = Number(item.customHeight || 0);
  const width = Number(item.customWidth || 0);

  if (height <= 0 || width <= 0) return 0;

  const printCost =
    ceilToStep((height / 100 + 0.05) * (width / 100 + 0.05), 0.2) * 25000;

  const baseDim = Math.ceil((height / 100 + width / 100) * 2 + 0.5);

  const innerWood = baseDim * 2000;
  const ailFrame = baseDim * 5500;
  const kasFrame = baseDim * 7500;

  let sell = printCost;

  if (item.frameOption === "إطار داخلي فقط") {
    sell = printCost + innerWood;
  }

  if (item.frameOption === "إطار كامل") {
    if (item.frameType === "L") {
      sell = printCost + innerWood + ailFrame;
    } else if (item.frameType === "Kas") {
      sell = printCost + innerWood + kasFrame;
    } else {
      sell = printCost + innerWood;
    }
  }

  if (item.panelType === "أوريجن") {
    return Math.round(sell * 2.5);
  }

  if (item.panelType === "إدشن") {
    return Math.round((sell + 50000) * 2.25);
  }

  if (item.panelType === "برايم") {
    return Math.round((sell + 75000) * 2.25);
  }

  return 0;
};
// wallpaperRates: سعر متر ورق الجدران المعتمد (GET /board-pricing)
const calculateItemPrice = (item, wallpaperRates = DEFAULT_WALLPAPER_RATES) => {
  let pricePerPanel = item.pricePerPanel || 0;

  const isCustomSize = item.sizeSource === "custom";

  const wasManualPriceEdited = item.manualPrice === true;

  // 🧻 ورق جدران: المساحة × سعر المتر (مع تركيب/بدون). السعر المعتمد ينحفظ
  // ويه البند (wallpaperRatePerM2) - منه تنحسب الكلفة بالإكسل وقيود الاعتماد
  if (isWallpaper(item)) {
    const wallpaperRatePerM2 = wallpaperRateFor(item, wallpaperRates);
    const { width, height } = getInvoiceItemDimensionsForCost(item);
    if (!wasManualPriceEdited && width > 0 && height > 0) {
      pricePerPanel = Math.round(wallpaperAreaM2(width, height) * wallpaperRatePerM2);
    }
    const quantity = Number(item.quantity || 0);
    return {
      ...item,
      wallpaperRatePerM2,
      pricePerPanel,
      total: Number(pricePerPanel || 0) * quantity,
    };
  }

  if (!wasManualPriceEdited) {
    if (isCustomSize) {
      const customPrice = calculateCustomPanelPrice(item);

      if (customPrice > 0) {
        pricePerPanel = customPrice;
      }
    } else {
      const canAutoPrice =
        item.sizeKey &&
        (item.panelType === "أوريجن" || item.panelType === "إدشن") &&
        item.frameType &&
        item.sizeSource === "list";

      if (canAutoPrice) {
        const typeKey = toPriceTypeKey(item.panelType);
        const framePrices =
          PRICE_MAP[item.frameType] &&
          PRICE_MAP[item.frameType][typeKey || "Origin"];

        if (framePrices && framePrices[item.sizeKey]) {
          pricePerPanel = framePrices[item.sizeKey];
        }
      }
    }
  }

  const quantity = Number(item.quantity || 0);
  const total = Number(pricePerPanel || 0) * quantity;

  return {
    ...item,
    pricePerPanel,
    total,
  };
};

// ===== حساب كلفة الإنتاج (طباعة + إطار) لعناصر الفاتورة - تُستخدم عند الاعتماد لترحيل القيود المحاسبية =====
const getInvoiceItemDimensionsForCost = (item) => {
  if (item.sizeSource === "custom") {
    return {
      height: Number(item.customHeight || 0),
      width: Number(item.customWidth || 0),
    };
  }

  if (item.sizeKey) {
    const [w, h] = item.sizeKey.split("x").map(Number);
    return {
      width: Number(w || 0),
      height: Number(h || 0),
    };
  }

  return { width: 0, height: 0 };
};

const calculateInvoiceProductionCosts = (invoiceData) => {
  return (invoiceData.items || []).reduce(
    (totals, item) => {
      const { width, height } = getInvoiceItemDimensionsForCost(item);
      const quantity = Number(item.quantity || 0);

      if (width <= 0 || height <= 0 || quantity <= 0) {
        return totals;
      }

      const printCost =
        ceilToStep((height / 100 + 0.05) * (width / 100 + 0.05), 0.2) * 25000;

      const baseDim = Math.ceil((height / 100 + width / 100) * 2 + 0.5);

      const innerWood = baseDim * 2000;
      const ailFrame = baseDim * 5500;
      const kasFrame = baseDim * 7500;

      let frameCost = 0;

      if (item.frameOption === "إطار داخلي فقط") {
        frameCost = innerWood;
      }

      if (item.frameOption === "إطار كامل") {
        if (item.frameType === "L") {
          frameCost = innerWood + ailFrame;
        } else if (item.frameType === "Kas") {
          frameCost = innerWood + kasFrame;
        } else {
          frameCost = innerWood;
        }
      }

      return {
        printTotal: totals.printTotal + printCost * quantity,
        frameTotal: totals.frameTotal + frameCost * quantity,
      };
    },
    { printTotal: 0, frameTotal: 0 }
  );
};

/* =============================
   2) Default Invoice Templates
   ============================= */

// 🤝 شركاء ينطون خصم على اللوحات بدل العمولة - نفس القائمة بالباك اند
// (invoiceController.js / orderController.js)
const PARTNER_DISCOUNT_SOURCES = ["ست سارة"];
const DEFAULT_PARTNER_DISCOUNT_PERCENT = 20;

const recalcInvoice = (inv) => {
  const subtotal = Number(inv.subtotal || 0);


  // ✅ الاستشارة
  const consultationFee = Number(inv.consultationFee || 0);

  // ✅ التركيب (25 + 5 لكل لوحة إضافية)

  // ✅ النقل (10 + 5 لكل لوحة)

// 🧻 ورق الجدران ما ينحسب بعدد اللوحات لأجور التركيب/النقل - تركيبه داخل
// بسعر المتر ("مع تركيب")
const totalPanels = inv.items?.reduce(
  (sum, item) => (isWallpaper(item) ? sum : sum + Number(item.quantity || 0)),
  0
);

// =======================
// AUTO CALCULATION
// =======================

const installationFeeAuto =
  totalPanels > 1
    ? 25000 + (totalPanels - 1) * 5000
    : totalPanels > 0
    ? 25000
    : 0;

const transportFeeAuto =
  totalPanels > 1
    ? 10000 + (totalPanels - 1) * 5000
    : totalPanels > 0
    ? 10000
    : 0;

// =======================
// MANUAL OVERRIDE
// =======================

const installationFee =
  inv.installationFeeManual !== undefined &&
  inv.installationFeeManual !== installationFeeAuto
    ? inv.installationFeeManual
    : installationFeeAuto;

const transportFee =
  inv.transportFeeManual !== undefined &&
  inv.transportFeeManual !== transportFeeAuto
    ? inv.transportFeeManual
    : transportFeeAuto;

  // ✅ زيارة الموقع
  const siteVisitFee = Number(inv.siteVisitFee || 0);

  const discount = Number(inv.discount || 0);

  // 🤝 خصم الشريك (ست سارة) - نسبة على اللوحات فقط (subtotal)، والأجور
  // (استشارة/تركيب/نقل/زيارة) تنضاف بعده بدون خصم. منفصل عن الخصم اليدوي.
  const partnerDiscountSource = PARTNER_DISCOUNT_SOURCES.includes(inv.partnerDiscountSource)
    ? inv.partnerDiscountSource
    : "";
  const partnerDiscountPercent = partnerDiscountSource
    ? Math.min(Math.max(Number(inv.partnerDiscountPercent) || 0, 0), 99)
    : 0;
  const partnerDiscountAmount = Math.round(subtotal * (partnerDiscountPercent / 100));

  const finalTotal =
    subtotal -
    partnerDiscountAmount +
    consultationFee +
    installationFee +
    transportFee +
    siteVisitFee -
    discount;

  // =====================
  // 💰 الدفع
  // =====================
  const paymentMethod = inv.paymentMethod || "cod";
let advancePayment =
  inv.advancePayment !== undefined && inv.advancePayment !== null
    ? Number(inv.advancePayment)
    : 0;

  if (paymentMethod === "full") {
    advancePayment = finalTotal;
  }

 if (paymentMethod === "partial") {
  if (inv.advancePayment === undefined || inv.advancePayment === null) {
    advancePayment = finalTotal * 0.5;
  }
}

  if (paymentMethod === "cod") {
    advancePayment = 0;
  }

  const remainingPayment = Math.max(0, finalTotal - advancePayment);

  return {
    ...inv,
    consultationFee,
    installationFee,
    transportFee,
    siteVisitFee,
    partnerDiscountSource,
    partnerDiscountPercent,
    partnerDiscountAmount,
    finalTotal,
    paymentMethod,
    advancePayment,
    remainingPayment,
  };
};


const defaultInvoice = {
  customerName: "",
  phone: "",
  projectName: "",
  companyName: "",
  invoiceNumber: "",
  governorate: "",
  nearestPoint: "",
  date: new Date().toISOString().substring(0, 10),
  items: [],
  subtotal: 0,

  consultationFee: 75000,   // ✅ ديفولت
  transportFee: 10000,
  installationFee: 25000,   // ✅ ديفولت
  siteVisitFee: 75000,      // ✅ جديد
  hideTransportFeeFromCustomer: false,

  notes: "", // ✅ ملاحظات اختيارية تكتبها الموظفة وتظهر بالفاتورة

  // ✅ تسميات نوع اللوحة كما تظهر بالفاتورة - تبدأ بالقيم الافتراضية وتكدر
  // الموظفة تعدلهن من نموذج الفاتورة
  panelTypeLabels: { ...PANEL_TYPE_INVOICE_LABELS },

  discount: 0,
  // 🤝 خصم الشريك على اللوحات فقط ("" = بدون شريك)
  partnerDiscountSource: "",
  partnerDiscountPercent: 0,
  partnerDiscountAmount: 0,
  finalTotal: 0,

  paymentMethod: "cod", // cod | partial | full

  advancePayment: 0,
  remainingPayment: 0,

  // ✅ حساب البيع (نفس خيارات AddOrder.js) - داخلي فقط، لا يظهر للزبون بالفاتورة
  saleAccount: "بيع",
};

const emptyItem = {
  manualPrice: false,
  panelName: "",
  panelType: "",
  panelShape: "",
  frameOption: "",
  frameType: "",
  customFrameType: "",
  sizeSource: "list",
  sizeKey: "",
  customHeight: "",
  customWidth: "",
    frameColor: "",   // 👈 جديد
    customFrameColor: "",
  quantity: 1,
  pricePerPanel: 0,
  total: 0,
  // 📷 صور اللوحة (يمكن أكثر من صورة لنفس اللوحة/القياس) - كل عنصر:
  // { id, file (محلي قبل الرفع أو null), preview (object URL محلي أو ""),
  //   url (رابط Storage بعد الرفع أو للنسخ المحفوظة سابقاً) }
  panelImages: [],
};

// يقرأ صور اللوحة بشكل موحّد بغض النظر عن الشكل القديم (panelImageUrl/
// panelImagePreview/panelImageFile مفردة) أو الجديد (panelImages مصفوفة) -
// يضمن أن الفواتير المحفوظة سابقاً بصورة واحدة تستمر تعمل بدون أي ترحيل بيانات
const getItemImages = (item) => {
  if (!item) return [];
  if (Array.isArray(item.panelImages)) return item.panelImages;
  if (item.panelImageUrl || item.panelImagePreview || item.panelImageFile) {
    return [
      {
        id: "legacy",
        file: item.panelImageFile || null,
        preview: item.panelImagePreview || "",
        url: item.panelImageUrl || "",
      },
    ];
  }
  return [];
};

/* =============================
   2-ب) أدوات ملف PDF لصفحات اللوحات (صورة الفاتورة + صفحة لكل لوحة)
   ============================= */

// تحويل رابط صورة مرفوعة مسبقاً (من نسخة قديمة عند التعديل) إلى dataURL
const urlToDataUrl = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// 🖼️ html2canvas ما يدعم object-fit (يمطّ الصورة على كامل الصندوق فتطلع
// مشوّهة)، وصور الموبايل اتجاهها محفوظ بـEXIF فممكن تطلع مكلوبة/مدوّرة.
// هنا نرسم الصورة بأنفسنا: نطبّق اتجاه EXIF، ونصغّرها لتدخل بالصندوق بنفس
// نسبة أبعادها (contain)، ونرجّع أبعادها الفعلية حتى الـHTML يعطيها
// width/height صريحة بدل الاعتماد على object-fit.
const loadOrientedImage = async (src) => {
  const blob = await (await fetch(src)).blob();
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch (err) {
      // متصفح ما يدعم الخيارات - نرجع لعنصر img العادي تحت
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
};

// القياس الحقيقي لكل صورة انفكّت لملف الـPDF (حسب src) - عرض سعر فاتورة
// المشروع يحتاجه للقص، فناخذه من هنا بدل ما نرجع ننزّل الصورة كاملة مرة ثانية
const decodedImageSizes = new Map();

const fitImageToBox = async (src, boxWidth, boxHeight) => {
  try {
    const image = await loadOrientedImage(src);
    const naturalWidth = image.width || image.naturalWidth;
    const naturalHeight = image.height || image.naturalHeight;
    if (!naturalWidth || !naturalHeight) throw new Error("empty image");
    decodedImageSizes.set(src, { width: naturalWidth, height: naturalHeight });

    const ratio = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight, 1);
    const width = Math.max(1, Math.round(naturalWidth * ratio));
    const height = Math.max(1, Math.round(naturalHeight * ratio));

    // ضعف الدقة لأن html2canvas يلتقط الصفحة بـscale: 2
    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    if (typeof image.close === "function") image.close();

    return { src: canvas.toDataURL("image/jpeg", 0.92), width, height };
  } catch (err) {
    console.error("تعذر تجهيز صورة اللوحة للـPDF:", err);
    return { src, width: boxWidth, height: boxHeight };
  }
};

// القياس الحقيقي لصورة مختارة محلياً (صور نماذج التسعيرة - للقص بعرض السعر)
const readImageFileSize = (file) =>
  new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      URL.revokeObjectURL(src);
      resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.onerror = () => {
      URL.revokeObjectURL(src);
      resolve({ width: 0, height: 0 });
    };
    probe.src = src;
  });

const getFrameTypeLabel = (item) => {
  const frameType = item?.frameType;
  if (frameType === "L") return "L Frame";
  if (frameType === "Kas") return "Kas Frame";
  if (frameType === "أخرى") return item?.customFrameType || "أخرى";
  return frameType || "—";
};

// صفحة PDF للوحة = نفس نسبة A4 (عرض 800px) حتى ولا صفحة تنقص وتنزل نص صورتها
// للصفحة الثانية (addCanvasAsPages يقص أي canvas أطول من صفحة)
const PANEL_PAGE_WIDTH_PX = 800;
const PANEL_PAGE_HEIGHT_PX = 1130;
const PANEL_IMAGES_GAP_PX = 16;
// حدود صندوق الصورة (1px من كل جهة)
const PANEL_IMAGE_BORDER_PX = 2;

const getImageNaturalSize = async (src) => {
  if (!decodedImageSizes.has(src)) await fitImageToBox(src, 10, 10);
  return decodedImageSizes.get(src) || { width: 1, height: 1 };
};

// ارتفاع صف الصور الطبيعي = أطول صورة بالصف بعد تصغيرها على عرض الخانة
// (بدون تكبير الصور الصغيرة)
const getNaturalRowHeight = (rowSizes, cellWidth) =>
  Math.max(
    ...rowSizes.map((size) => {
      const innerWidth = cellWidth - PANEL_IMAGE_BORDER_PX;
      const ratio = Math.min(innerWidth / size.width, 1);
      return Math.ceil(size.height * ratio) + PANEL_IMAGE_BORDER_PX;
    })
  );

const buildPanelImageRowsHtml = async (rows, cellWidth) => {
  const rowsHtml = await Promise.all(
    rows.map(async ({ images, height }) => {
      const fittedImages = await Promise.all(
        images.map((src) =>
          fitImageToBox(
            src,
            cellWidth - PANEL_IMAGE_BORDER_PX,
            height - PANEL_IMAGE_BORDER_PX
          )
        )
      );
      const cells = fittedImages
        .map(
          (fitted) => `
          <div style="width:${cellWidth}px; height:${height}px; box-sizing:border-box; border-radius:8px; border:1px solid #ddd; overflow:hidden; display:flex; align-items:center; justify-content:center;">
            <img src="${fitted.src}" width="${fitted.width}" height="${fitted.height}" style="width:${fitted.width}px; height:${fitted.height}px;" />
          </div>
        `
        )
        .join("");
      return `
        <div style="display:flex; flex-direction:row; gap:${PANEL_IMAGES_GAP_PX}px; justify-content:center;">
          ${cells}
        </div>
      `;
    })
  );
  return `
    <div style="display:flex; flex-direction:column; gap:${PANEL_IMAGES_GAP_PX}px;">
      ${rowsHtml.join("")}
    </div>
  `;
};

// يبني صفحات تفاصيل لوحة واحدة (قياس + عدد + نوع/لون الإطار + صورها) كـ
// canvases جاهزة للإضافة بملف الـ PDF - جدول التفاصيل بأول صفحة فقط.
// لوحة بيها أكثر من صورة: كل صورتين جنب بعض (مقابيل بعض، مو وحدة تحت
// الثانية)، ونملي الصفحة بأكثر عدد صفوف يلزم بيها - الصفوف اللي ما تلزم
// تنتقل لصفحة "تابع" جديدة، فولا صورة تنقص بين صفحتين ولا يبقى فراغ كبير.
// لوحة بصورة وحدة بس تاخذ عرض الصفحة كله
// - يسوي render لـ div مخفي خارج الشاشة عشان html2canvas يقدر يلتقطه
const renderPanelDetailPageCanvases = async (
  item,
  index,
  imageDataUrls,
  panelTypeLabels
) => {
  const images = imageDataUrls || [];
  const perRow = images.length > 1 ? 2 : 1;
  const sizes = await Promise.all(images.map(getImageNaturalSize));
  const pendingRows = [];
  for (let i = 0; i < images.length; i += perRow) {
    pendingRows.push({
      images: images.slice(i, i + perRow),
      sizes: sizes.slice(i, i + perRow),
    });
  }

  const canvases = [];
  let pageIndex = 0;
  do {
    const container = createPanelPageContainer(
      item,
      index,
      panelTypeLabels,
      pageIndex === 0
    );
    document.body.appendChild(container);
    try {
      // المساحة الباقية للصور بعد العنوان والجدول والفوتر - نقيسها فعلياً
      // حتى الصفحة ما تطول أكثر من A4
      const imagesArea = container.querySelector("[data-panel-images]");
      const areaWidth = imagesArea.clientWidth;
      const areaHeight = imagesArea.clientHeight;
      const cellWidth =
        perRow === 2
          ? Math.floor((areaWidth - PANEL_IMAGES_GAP_PX) / 2)
          : areaWidth;

      if (images.length === 0) {
        imagesArea.innerHTML = `<div style="text-align:center; color:#999; padding:60px 0;">بدون صورة مرفقة لهذه اللوحة</div>`;
      } else {
        const pageRows = [];
        let usedHeight = 0;
        while (pendingRows.length) {
          const naturalHeight = getNaturalRowHeight(pendingRows[0].sizes, cellWidth);
          const neededHeight =
            (pageRows.length ? PANEL_IMAGES_GAP_PX : 0) + naturalHeight;
          if (pageRows.length && usedHeight + neededHeight > areaHeight) break;
          // أول صف بالصفحة ينضاف دائماً (يصغر على المساحة لو أطول منها)
          const height = Math.min(naturalHeight, areaHeight - usedHeight);
          pageRows.push({ images: pendingRows.shift().images, height });
          usedHeight += (pageRows.length > 1 ? PANEL_IMAGES_GAP_PX : 0) + height;
        }
        // eslint-disable-next-line no-await-in-loop
        imagesArea.innerHTML = await buildPanelImageRowsHtml(pageRows, cellWidth);
      }

      // eslint-disable-next-line no-await-in-loop
      canvases.push(await html2canvas(container, { scale: 2, useCORS: true }));
    } finally {
      document.body.removeChild(container);
    }
    pageIndex += 1;
  } while (pendingRows.length);

  return canvases;
};

const createPanelPageContainer = (item, index, panelTypeLabels, isFirstPage) => {
  const frameColor =
    item.frameColor === "أخرى" ? item.customFrameColor : item.frameColor;

  const container = document.createElement("div");
  container.dir = "rtl";
  container.style.cssText = `
    position: fixed;
    top: -10000px;
    left: -10000px;
    width: ${PANEL_PAGE_WIDTH_PX}px;
    height: ${PANEL_PAGE_HEIGHT_PX}px;
    padding: 40px;
    background: #ffffff;
    font-family: Tahoma, Arial, sans-serif;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `;

  container.innerHTML = `
    <h2 style="text-align:center; margin:0 0 24px;">لوحة رقم ${index + 1}${
    item.panelName ? `: ${item.panelName}` : ""
  }${isFirstPage ? "" : " (تابع)"}</h2>
    ${isFirstPage ? `<table style="width:100%; border-collapse:collapse; font-size:16px; margin-bottom:24px;">
      <tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd; width:35%;">قياس اللوحة</td>
        <td style="padding:10px; border:1px solid #ddd;">${(item.sizeKey || "—").replace("x", " * ")}</td>
      </tr>
      <tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd;">العدد</td>
        <td style="padding:10px; border:1px solid #ddd;">${item.quantity || "—"}</td>
      </tr>
      <tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd;">نوع اللوحة</td>
        <td style="padding:10px; border:1px solid #ddd;">${resolvePanelTypeLabel(item.panelType, panelTypeLabels) || "—"}</td>
      </tr>
      ${
        isWallpaper(item)
          ? `<tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd;">التركيب</td>
        <td style="padding:10px; border:1px solid #ddd;">${wallpaperInstallLabel(item)}</td>
      </tr>`
          : `<tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd;">نوع الإطار</td>
        <td style="padding:10px; border:1px solid #ddd;">${getFrameTypeLabel(item)}</td>
      </tr>
      <tr>
        <td style="padding:10px; font-weight:bold; border:1px solid #ddd;">لون الإطار</td>
        <td style="padding:10px; border:1px solid #ddd;">${frameColor || "—"}</td>
      </tr>`
      }
    </table>` : ""}
    <div data-panel-images style="flex:1; min-height:0;"></div>
    <div style="margin-top:28px; padding-top:8px; border-top:1px solid #ddd; font-size:11px; line-height:1.7; color:#888; text-align:center;">
      <span style="font-weight:bold; color:#666;">© ${COPYRIGHT_TITLE}</span> - ${COPYRIGHT_TEXT}
    </div>
  `;

  return container;
};

// ✅ يضيف canvas قد يكون أطول من صفحة A4 واحدة كعدة صفحات متتالية. الكود
// القديم كان يرسم الصورة كاملة بارتفاعها الحقيقي (المحسوب من نسبة العرض
// للارتفاع) بصفحة واحدة فقط عبر addImage - أي جزء يتجاوز ارتفاع الصفحة
// الفيزيائي (297mm) يقع خارج حدود الصفحة ولا يظهر بالـPDF إطلاقاً، وهذا
// يصير بالضبط لما تزيد عدد "لوحات" الفاتورة (الجدول يطول أكثر من صفحة).
// نقصّ الـcanvas الأصلي لشرائح بارتفاع صفحة واحدة بالبكسل (بنفس معامل
// تحويل العرض/mm) ونضيف كل شريحة كصورة منفصلة على صفحتها الخاصة، فتظهر كل
// القيود بغض النظر عن عددها.
// format "JPEG" لصفحات اللوحات (صور): jsPDF يضغط PNG بالجافاسكربت ببطء شديد
// والملف يطلع ضخم مع الصور، أما JPEG ينضاف مباشرة. صفحة الفاتورة نفسها تبقى
// PNG حتى يبقى النص حاد
const isCanvasBlank = (canvas) => {
  const { data } = canvas
    .getContext("2d")
    .getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245) return false;
  }
  return true;
};

const addCanvasAsPages = (pdf, canvas, pdfWidth, isFirstPage, format = "PNG") => {
  const pageHeightMM = pdf.internal.pageSize.getHeight();
  const pxPerMm = canvas.width / pdfWidth;
  const pageHeightPx = Math.max(1, Math.floor(pageHeightMM * pxPerMm));

  let renderedPx = 0;
  let firstSlice = true;

  while (renderedPx < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    sliceCanvas
      .getContext("2d")
      .drawImage(
        canvas,
        0,
        renderedPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );

    // شريحة أخيرة كلها بيضة (الفاتورة أطول من A4 بشوية فراغ تحت) ما نسويلها
    // صفحة - هاي اللي كانت تطلّع صفحة فارغة بعد صفحة الفاتورة
    if (!firstSlice && isCanvasBlank(sliceCanvas)) {
      break;
    }

    if (!(isFirstPage && firstSlice)) {
      pdf.addPage();
    }
    const sliceHeightMM = (sliceHeightPx * pdfWidth) / canvas.width;
    if (format === "JPEG") {
      pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.9), "JPEG", 0, 0, pdfWidth, sliceHeightMM);
    } else {
      pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", 0, 0, pdfWidth, sliceHeightMM);
    }

    renderedPx += sliceHeightPx;
    firstSlice = false;
  }
};

// يبني ملف PDF كامل: صفحة أولى = mainCanvas (صورة الفاتورة، مقسّمة على عدة
// صفحات لو طالت) + صفحة (أو أكثر) لكل عنصر (كل صورها مدمجة بنفس الصفحة) -
// يستخدم بالحفظ الفعلي وبزر التحميل المحلي بنفس الوقت حتى الاثنين ينتجون
// نفس النتيجة
// mainFormat: صيغة صفحة الفاتورة نفسها - "PNG" (الافتراضي، فواتير الموظفات
// بدون تغيير) أو "JPEG" (فورم ارت تاتش: أسرع بكثير وملف أصغر بالموبايل)
const buildMultiPagePdf = async (mainCanvas, itemsWithImageData, panelTypeLabels, mainFormat = "PNG") => {
  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  addCanvasAsPages(pdf, mainCanvas, pdfWidth, true, mainFormat);

  for (let i = 0; i < itemsWithImageData.length; i++) {
    const { item, imageDataUrls } = itemsWithImageData[i];
    // eslint-disable-next-line no-await-in-loop
    const itemCanvases = await renderPanelDetailPageCanvases(
      item,
      i,
      imageDataUrls || [],
      panelTypeLabels
    );
    itemCanvases.forEach((itemCanvas) =>
      addCanvasAsPages(pdf, itemCanvas, pdfWidth, false, "JPEG")
    );
  }

  return pdf;
};

// اسم ملف آمن مبني على اسم الزبون (يشيل الرموز الممنوعة بأسماء الملفات)
const buildSafeInvoiceFileName = (customerName) => {
  const cleaned = String(customerName || "فاتورة أرتيكا")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-");
  return `${cleaned || "فاتورة أرتيكا"}.pdf`;
};

/* =============================
   3) MAIN PAGE — with Versions
   ============================= */
const FOOTER_INFO = {
  email: "Info@artika.ai",
  phone: "0783-448-3330",
  website: "www.artika.ai",
  location: "Iraq - Erbil - Cihan la Gulan complex",
};

const InvoicePage = () => {
  // ✅ يحفظ آخر رقم فاتورة اتولّد تلقائيًا (معاينة فقط) حتى نعرف وقت الحفظ إذا
  // الموظفة عدّلت الرقم يدويًا أو لا - انظري assignSequentialInvoiceNumber تحت
  const autoNumberRef = useRef(null);

  // ⚠️ Dead code منذ Phase 07-C2b: تخصيص رقم الفاتورة الفعلي صار سيرفرياً
  // بالكامل (POST /invoices بالباك اند، بمعاملة واحدة تجمع العدّاد + إنشاء
  // الفاتورة + أول نسخة). لا أحد يستدعي هذي الدالة بعد الآن - أُبقيت بدون
  // حذف حسب سياسة المشروع (توثيق بدل حذف الكود الميت)، وليست إعادة استخدام
  // مقصودة لاحقاً.
  // eslint-disable-next-line no-unused-vars
  const assignSequentialInvoiceNumber = async (year) => {
    const counterRef = doc(db, "counters", `invoiceSerial_${year}`);

    // ✅ أول استخدام لهذا العدّاد (وثيقة العدّاد ما انسوت بعد): نجيب أعلى رقم
    // فاتورة موجود فعلاً لنفس السنة كنقطة بداية، حتى ما نرجع نبدأ من 1 ونصطدم
    // بأرقام سابقة اتولّدت بالطريقة القديمة
    let fallbackSerial = 0;
    const counterCheckSnap = await getDoc(counterRef);
    if (!counterCheckSnap.exists()) {
      const q = query(
        collection(db, "invoices"),
        orderBy("invoiceNumber", "desc"),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const lastNumber = snap.docs[0].data().invoiceNumber || "";
        if (lastNumber.startsWith(String(year))) {
          fallbackSerial = parseInt(lastNumber.slice(4)) || 0;
        }
      }
    }

    const newSerial = await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);
      const lastSerial = counterSnap.exists()
        ? Number(counterSnap.data().lastSerial || 0)
        : fallbackSerial;
      const next = lastSerial + 1;
      transaction.set(counterRef, { lastSerial: next }, { merge: true });
      return next;
    });

    return `${year}${String(newSerial).padStart(4, "0")}`;
  };

  const generateInvoiceNumber = async () => {
  const year = new Date().getFullYear();

  const q = query(
    collection(db, "invoices"),
    orderBy("invoiceNumber", "desc"),
    limit(1)
  );

  const snap = await getDocs(q);

  if (!snap.empty) {
    const lastNumber = snap.docs[0].data().invoiceNumber;

    const lastSerial = parseInt(lastNumber.slice(4));
    const newSerial = lastSerial + 1;

    const formattedSerial = String(newSerial).padStart(4, "0");
    const previewNumber = `${year}${formattedSerial}`;

    autoNumberRef.current = previewNumber;
    setInvoice((prev) => ({
      ...prev,
      invoiceNumber: previewNumber,
    }));
  } else {
    const previewNumber = `${year}0001`;
    autoNumberRef.current = previewNumber;
    setInvoice((prev) => ({
      ...prev,
      invoiceNumber: previewNumber,
    }));
  }
};
  const { invoiceId, versionId } = useParams(); // إذا موجود → تعديل / إنشاء نسخة جديدة
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(defaultInvoice);
  const [currentItem, setCurrentItem] = useState(emptyItem);
  // 🧻 سعر متر ورق الجدران المعتمد - نفس واجهة حساب سعر اللوحة
  const [wallpaperRates, setWallpaperRates] = useState(DEFAULT_WALLPAPER_RATES);
  useEffect(() => {
    let alive = true;
    apiFetch(`${API_BASE}/board-pricing`)
      .then((res) => (res.ok ? res.json() : null))
      .then((pricing) => alive && pricing && setWallpaperRates(wallpaperRatesFromPricing(pricing)))
      .catch((err) => console.error("Failed to load wallpaper pricing:", err));
    return () => {
      alive = false;
    };
  }, []);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [currentVersionNumber, setCurrentVersionNumber] = useState(null);
  // 🤝 مصدر الفاتورة إذا جاية من "طلب جديد" مال ارت تاتش (من مستند الفاتورة
  // الرئيسي) - يروح ويا الاعتماد لـAddOrder حتى الطلب ينضاف بمصدر ارت تاتش
  const [partnerSource, setPartnerSource] = useState("");
  // 📐 منشئ فاتورة المشروع (من مستند الفاتورة الرئيسي) - لليبل "مشروع من
  // المدير" / "مشروع بنين"
  const [invoiceCreatedBy, setInvoiceCreatedBy] = useState("");
  // 🏗️ بوكس "فاتورة مشروع": نفس فورم ومنطق الفاتورة العادية بالضبط، بس
  // الفاتورة تنحفظ بـinvoiceSource="projectInvoice" واسم المشروع إجباري
  const [projectInvoiceFlag, setProjectInvoiceFlag] = useState(false);
  const [searchParams] = useSearchParams();
  // 📋 تسعيرة المشروع (مشروع كبير): فاتورة نوعها "projectQuote" - نفس فورم
  // فاتورة المشروع بدون تفاصيل اللوحات (الشكل/نوع ولون الإطار/الصور) + صور
  // نماذج منفصلة (sampleImages بالنسخة). الحفظ يطلع عرض سعر مثل فاتورة المشروع،
  // و"اعتمادها" من العرض يحوّلها لنفس الفاتورة كفاتورة مشروع تكمل تفاصيلها
  const [quoteMode, setQuoteMode] = useState(false);
  const [sampleImages, setSampleImages] = useState([]); // { id, file, preview, url, width, height }
  // مصدر الفاتورة المحفوظة ("projectCost" = مشروع المدير من حساب كلفة المشروع)
  const [invoiceSource, setInvoiceSource] = useState("");
  // ✅ حقل التاريخ مخفي افتراضياً بالنموذج (يبقى تاريخ اليوم تلقائياً مثل
  // رقم الفاتورة) - لكن بخلاف رقم الفاتورة، التاريخ فعلياً قابل للتعديل
  // (مثلاً لتسجيل فاتورة بتاريخ سابق)، فنضيف رابط صغير "تغيير التاريخ" يظهر
  // الحقل عند الحاجة فقط بدل إخفائه نهائياً
  const [showDateField, setShowDateField] = useState(false);

  const previewRef = useRef(null);
  // ✅ مرجع مستقل عن useParams: يحدَّد فور نجاح إنشاء فاتورة جديدة (قبل أي
  // navigate/re-render)، حتى لو ضغطت الموظفة "حفظ" مرة ثانية بنفس الجلسة قبل
  // ما ينتقل الرابط لوضع التعديل، تُعامَل الفاتورة كـ"موجودة مسبقاً" (نسخة
  // جديدة) بدل إنشاء فاتورة مكررة برقم جديد بالكامل - هذا بالضبط السبب اللي
  // ولّد فواتير مكررة فعلياً (نفس الزبون/الهاتف/العنوان برقمين متتاليين).
  const activeInvoiceIdRef = useRef(invoiceId || null);

  const username = localStorage.getItem("username");
  // ⚠️ Dead code منذ Phase 07-C2b: كانت تُستخدَم فقط كـcreatedBy عند إنشاء
  // فاتورة/نسخة جديدة (اسم عربي معروض بدل username الخام). بعد نقل الإنشاء
  // للباك اند، createdBy/createdByUsername صارا يُشتقّان من req.identity
  // سيرفرياً (تعليمات صريحة بهذي المرحلة) - فلا حاجة لهذا الجدول هنا بعد
  // الآن. أُبقيت بدون حذف حسب سياسة المشروع (توثيق بدل حذف الكود الميت).
  // eslint-disable-next-line no-unused-vars
  const employeeName =
    username === "rand@123"
      ? "رند"
      : username === "jumana@123"
      ? "جمانا"
      : username === "karena@123"
      ? "تيهان"
      : username === "Baneen@123"
      ? "بنين"
      : username === "maysa@123"
      ? "مياسة"
        : username === "farah@123"
      ? "فرح"
      : "موظف";

  /* =============================
       تحميل آخر نسخة إذا invoiceId موجود
     ============================= */

     useEffect(() => {
  if (!invoiceId) {
    generateInvoiceNumber();
  }
}, []);
  // يبقي activeInvoiceIdRef متزامناً مع رابط الصفحة الفعلي (مثلاً عند فتح
  // فاتورة أخرى بنفس التبويب لاحقاً)، بينما handleSaveInvoice يقدر يحدّثه
  // فوراً بدون انتظار أي navigate/re-render (راجع تعريف المرجع بالأعلى).
  useEffect(() => {
    activeInvoiceIdRef.current = invoiceId || null;
  }, [invoiceId]);
 useEffect(() => {
  const loadInvoice = async () => {
    if (!invoiceId) return;

    try {
      setLoading(true);

      // 1) تأكد الفاتورة موجودة
      const invoiceRef = doc(db, "invoices", invoiceId);
      const parentSnap = await getDoc(invoiceRef);

      if (!parentSnap.exists()) {
        console.warn("Invoice not found");
        return;
      }

      // 🔒 بمجرد اعتماد أي نسخة لهذه الفاتورة، تصير كل نسخها (المعتمدة
      // والسابقة) غير قابلة للتعديل نهائياً - لحماية تطابق كلفة الفواتير
      // بسستم الحسابات مع اكسل الفواتير المعتمدة. ✅ Phase 07-D: فحص !=null
      // بدل truthy - يقبل approvedVersionNumber=0 (أول نسخة) كـ"معتمدة فعلاً"
      // بدل تجاهله بصمت.
      const approvedVersionNumberValue = parentSnap.data().approvedVersionNumber;
      if (approvedVersionNumberValue !== undefined && approvedVersionNumberValue !== null) {
        alert("🔒 هذه الفاتورة معتمدة بالفعل ولا يمكن تعديلها.");
        navigate(`/invoice/${invoiceId}`);
        return;
      }

      setPartnerSource(parentSnap.data().partnerSource || "");
      setInvoiceCreatedBy(parentSnap.data().createdBy || "");
      const loadedSource = parentSnap.data().invoiceSource;
      setProjectInvoiceFlag(loadedSource === "projectInvoice" || loadedSource === "projectQuote");
      setQuoteMode(loadedSource === "projectQuote");
      setInvoiceSource(parentSnap.data().invoiceSource || "");

      // 2) إذا جايين من "تعديل نسخة" → حمّل نسخة محددة
      if (versionId) {
        const verRef = doc(db, "invoices", invoiceId, "versions", versionId);
        const verSnap = await getDoc(verRef);

        if (!verSnap.exists()) {
          alert("❌ النسخة غير موجودة");
          return navigate(-1);
        }

        const v = verSnap.data();
        const data = v.data || {};
        setInvoice({
          ...data,
          panelTypeLabels: {
            ...PANEL_TYPE_INVOICE_LABELS,
            ...(data.panelTypeLabels || {}),
          },
        });
        setCurrentVersionNumber(v.versionNumber ?? null);
        setSampleImages(
          (data.sampleImages || []).map((img) => ({ id: img.url, file: null, preview: "", ...img }))
        );
        return;
      }

      // 3) غير ذلك → حمّل آخر نسخة
      const versionsCol = collection(invoiceRef, "versions");
      const q = query(versionsCol, orderBy("versionNumber", "desc"), limit(1));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const latest = snap.docs[0].data();
        if (latest.data) {
          setInvoice({
            ...latest.data,
            panelTypeLabels: {
              ...PANEL_TYPE_INVOICE_LABELS,
              ...(latest.data.panelTypeLabels || {}),
            },
          });
          setCurrentVersionNumber(latest.versionNumber ?? null);
        setSampleImages(
          (latest.data.sampleImages || []).map((img) => ({ id: img.url, file: null, preview: "", ...img }))
        );
        }
      }
    } catch (err) {
      console.error("Error loading invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  loadInvoice();
}, [invoiceId, versionId, navigate]);

  /* =============================
       📐 فاتورة مشروع
     ============================= */
  // isProjectMode = فاتورة مشروع قديمة بجدول "بنود المشروع" (projectItems) -
  // تبقى تنفتح وتتعدل بجدولها. الفواتير الجديدة ما تدخل هذا الوضع: البوكس
  // صار يعلّم الفاتورة مشروع فقط وتبقى بنفس فورم الفاتورة العادية
  const isProjectMode = Array.isArray(invoice.projectItems);
  const isProjectInvoice = projectInvoiceFlag || isProjectMode;
  // فواتير المشاريع اللي تطلع عرض سعر بعد الحفظ (بدل سؤال الاعتماد): فاتورة
  // مشروع بنين + مشروع المدير المرسَل من حساب كلفة المشروع
  const goesToProposal = projectInvoiceFlag || invoiceSource === "projectCost";
  const canCreateProject = PROJECT_INVOICE_USERS.includes(username);
  const projectResults = isProjectMode
    ? computeProjectResults(invoice.projectItems)
    : null;

  // رابط "إنشاء مشروع" بالقائمة (/invoice?project=1) يفتح الفورم والبوكس مفعّل
  useEffect(() => {
    if (!invoiceId && canCreateProject && searchParams.get("project") === "1") {
      setProjectInvoiceFlag(true);
      // "+ تسعيرة مشروع" بقائمة عروض الأسعار (/invoice?project=1&mode=quote)
      if (searchParams.get("mode") === "quote") setQuoteMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, searchParams]);

  const handleSampleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setSampleImages((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: URL.createObjectURL(file),
        url: "",
      })),
    ]);
  };

  // بنود التسعيرة المعتمدة (تحوّلت لفاتورة مشروع) جاية بدون تفاصيل - لازم تكتمل قبل الحفظ
  const itemMissingDetails = (item) =>
    (!isWallpaper(item) && !item.panelShape) ||
    getItemImages(item).length === 0 ||
    (item.frameOption === "إطار كامل" && (!item.frameType || !item.frameColor));

  const handleToggleProjectMode = (e) => {
    setProjectInvoiceFlag(e.target.checked);
    // فاتورة المشروع بدون اختيار "حساب البيع" - دائماً "بيع"
    if (e.target.checked) {
      setInvoice((prev) => ({ ...prev, saleAccount: "بيع" }));
    } else {
      // التسعيرة جزء من فاتورة المشروع فقط
      setQuoteMode(false);
    }
  };

  const updateProjectItems = (updater) => {
    setInvoice((prev) =>
      applyProjectItemsToInvoice(prev, updater(prev.projectItems || []))
    );
  };

  const updateProjectRow = (idx, field, value) => {
    updateProjectItems((rows) =>
      rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  };

  // الموظفة تكتب سعر البيع النهائي (سعر المشروع) - نخزنه كسعر بيع يدوي
  // مقسوم على المعامل حتى يبقى سعر اللوحة/البيع/المشروع متطابق بواجهة المدير
  const updateProjectRowPrice = (idx, value) => {
    updateProjectItems((rows) =>
      rows.map((row, i) => {
        if (i !== idx) return row;
        if (value === "") return { ...row, manualSalePrice: "" };
        return {
          ...row,
          manualSalePrice: toNumber(value) / getProjectRowFactor(row),
        };
      })
    );
  };

  const addProjectRow = () => {
    updateProjectItems((rows) => [
      ...rows,
      createProjectItem(rows.length + 1, 1),
    ]);
  };

  const removeProjectRow = (idx) => {
    updateProjectItems((rows) => {
      const next = rows
        .filter((_, i) => i !== idx)
        .map((row, i) => ({ ...row, name: i + 1 }));
      return next.length ? next : [createProjectItem(1, 1)];
    });
  };

  // 📷 صور بند المشروع - نفس شكل صور اللوحة بالفاتورة العادية (أكثر من
  // صورة لنفس البند، ترتفع لـStorage وقت الحفظ وتظهر بصفحة البند بالـPDF)
  const addProjectRowImages = (idx, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const newImages = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
      url: "",
    }));

    updateProjectItems((rows) =>
      rows.map((row, i) =>
        i === idx
          ? { ...row, panelImages: [...getItemImages(row), ...newImages] }
          : row
      )
    );
  };

  // بالترتيب مو بالـid - الصور المحفوظة من نسخة سابقة تنحفظ { url } فقط
  const removeProjectRowImage = (idx, imageIndex) => {
    updateProjectItems((rows) =>
      rows.map((row, i) =>
        i === idx
          ? {
              ...row,
              panelImages: getItemImages(row).filter((_, j) => j !== imageIndex),
            }
          : row
      )
    );
  };

  /* =============================
       4) تغيير بيانات الهيدر
     ============================= */
const handleHeaderChange = (e) => {
  const { name, value } = e.target;

  setInvoice((prev) => {
    const updated = { ...prev };

const numericFields = [
  "consultationFee",
  "transportFee",
  "installationFee",
  "siteVisitFee",
  "discount",
  "partnerDiscountPercent",
  "advancePayment",
];
  if (name === "installationFee") {
  updated.installationFeeManual = Number(value || 0);
} else if (name === "transportFee") {
  updated.transportFeeManual = Number(value || 0);
} else if (numericFields.includes(name)) {
  updated[name] = Number(value || 0);
} else {
  updated[name] = value;
}

    const recalced = recalcInvoice(updated);

    // إذا اختار "دفعة كاملة" نخلي المقدمة = النهائي تلقائياً
   // بعد: const recalced = recalcInvoice(updated);

if (name === "paymentMethod") {
  if (value === "full") {
    // دفعة كاملة → المقدمة = النهائي
    return recalcInvoice({ ...recalced, advancePayment: recalced.finalTotal });
  }

  if (value === "partial") {
    // دفعة جزئية → فرّغ/صفّر المقدمة
    return recalcInvoice({ ...recalced, advancePayment: 0 });
  }
}

    return recalced;
  });
};

  /* =============================
       5) تغيير بيانات اللوحة (السطر)
     ============================= */
  const handleItemChange = (e) => {
    const { name, value } = e.target;

    setCurrentItem((prev) => {
      let updated = { ...prev };

     if (name === "quantity" || name === "pricePerPanel") {
  updated[name] = Number(value || 0);

  if (name === "pricePerPanel") {
    updated.manualPrice = true;
  }
} else {
  updated[name] = value;
}
// 🧻 ورق جدران: بدون إطار (تنمسح كل تفاصيل الإطار) + مع تركيب افتراضياً.
// الرجوع لنوع ثاني يرجّع ديفولت الإطار (إطار كامل + L)
if (name === "panelType" && isWallpaper(updated) !== isWallpaper(prev)) {
  updated.manualPrice = false;
  updated.customFrameType = "";
  updated.frameColor = "";
  updated.customFrameColor = "";
  updated.sizeKey = "";
  updated.customHeight = "";
  updated.customWidth = "";
  updated.pricePerPanel = 0;
  updated.total = 0;
  if (isWallpaper(updated)) {
    // ورق الجدران: طول × عرض بس (بدون قائمة قياسات وبدون شكل لوحة)
    updated.frameOption = "بدون إطار";
    updated.frameType = "";
    updated.wallpaperInstall = true;
    updated.sizeSource = "custom";
    updated.panelShape = "";
  } else {
    updated.frameOption = "إطار كامل";
    updated.frameType = "L";
    updated.sizeSource = "list";
    delete updated.wallpaperInstall;
    delete updated.wallpaperRatePerM2;
  }
}
if (name === "wallpaperInstall") {
  updated.wallpaperInstall = value === "with";
  updated.manualPrice = false;
}
if (name === "panelShape") {
  updated.sizeSource = "list";
  updated.sizeKey = "";
  updated.customHeight = "";
  updated.customWidth = "";
  updated.pricePerPanel = 0;
  updated.manualPrice = false;
  updated.total = 0;
}
      // حذف نوع الإطار إذا لم تكن "إطار كامل"
if (name === "frameOption" && value !== "إطار كامل") {
  updated.frameType = "";
  updated.customFrameType = "";
  updated.frameColor = "";
  updated.customFrameColor = "";
}

if (name === "frameType") {
  updated.frameColor = "";
  updated.customFrameColor = "";
  if (value !== "أخرى") {
    updated.customFrameType = "";
  }
}

if (name === "frameColor" && value !== "أخرى") {
  updated.customFrameColor = "";
}

// 📋 التسعيرة بدون نوع إطار: "إطار كامل" ينحسب بسعر L (الأكثر استخداماً)
if (quoteMode && name === "frameOption" && value === "إطار كامل") {
  updated.frameType = "L";
}

      // اختيار قياس من القائمة أو أخرى (يدوي)
      if (name === "sizeKey") {
    if (value === "custom") {
  updated.sizeSource = "custom";
  updated.sizeKey = "";
  updated.customHeight = "";
  updated.customWidth = "";
  updated.pricePerPanel = 0;
  updated.manualPrice = false;
}else {
          updated.sizeSource = "list";
          updated.sizeKey = value;
            updated.manualPrice = false;

        }
      }

      updated = calculateItemPrice(updated, wallpaperRates);
      return updated;
    });
  };

  /* =============================
       6) التغيير للقياس اليدوي (الطول/العرض)
     ============================= */
const handleCustomSizeChange = (field, value) => {
  setCurrentItem((prev) => {
    const updated = {
      ...prev,
      [field]: value,
      sizeSource: "custom",
      manualPrice: false,
    };

    const height = field === "customHeight" ? value : prev.customHeight;
    const width = field === "customWidth" ? value : prev.customWidth;

    updated.sizeKey = width && height ? `${width}x${height}` : "";

    return calculateItemPrice(updated, wallpaperRates);
  });
};

  /* =============================
       6-ب) صورة اللوحة (تظهر بصفحة خاصة فيها بملف الفاتورة PDF)
     ============================= */
  const handlePanelImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newImages = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
      url: "",
    }));

    setCurrentItem((prev) => ({
      ...prev,
      panelImages: [...getItemImages(prev), ...newImages],
    }));
  };

  const removePanelImage = (imageId) => {
    setCurrentItem((prev) => ({
      ...prev,
      panelImages: getItemImages(prev).filter((img) => img.id !== imageId),
    }));
  };

  /* =============================
       7) إضافة لوحة جديدة
     ============================= */
 const addItem = () => {
  if (!currentItem.panelName.trim()) {
    alert("✔️ يجب إدخال اسم اللوحة");
    return;
  }

  if (!currentItem.panelType) {
    alert("✔️ يجب اختيار نوع اللوحة");
    return;
  }
// 📋 التسعيرة بدون شكل اللوحة/نوع ولون الإطار/الصور
if (!quoteMode && !isWallpaper(currentItem) && !currentItem.panelShape) {
  alert("✔️ يجب اختيار شكل اللوحة");
  return;
}
  if (!currentItem.frameOption) {
    alert("✔️ يجب اختيار خيار الإطار");
    return;
  }

  if (!quoteMode && currentItem.frameOption === "إطار كامل") {
    if (!currentItem.frameType) {
      alert("✔️ يجب اختيار نوع الإطار");
      return;
    }

    if (
      currentItem.frameType === "أخرى" &&
      !currentItem.customFrameType.trim()
    ) {
      alert("✔️ يجب كتابة نوع الإطار");
      return;
    }

    if (!currentItem.frameColor) {
      alert("✔️ يجب اختيار لون الإطار");
      return;
    }
    if (
  currentItem.frameColor === "أخرى" &&
  !currentItem.customFrameColor.trim()
) {
  alert("✔️ يجب كتابة لون الإطار");
  return;
}
  }

  if (!currentItem.sizeKey) {
    alert("✔️ يرجى إدخال القياس من القائمة أو قياس يدوي");
    return;
  }

  if (currentItem.sizeSource === "custom") {
    if (!currentItem.customHeight || !currentItem.customWidth) {
      alert("✔️ يجب إدخال الطول والعرض للقياس اليدوي");
      return;
    }
  }

  if (!currentItem.quantity || Number(currentItem.quantity) <= 0) {
    alert("✔️ يجب إدخال عدد صحيح أكبر من صفر");
    return;
  }

  if (!currentItem.pricePerPanel || Number(currentItem.pricePerPanel) <= 0) {
    alert("✔️ يجب إدخال سعر اللوحة");
    return;
  }

  if (!quoteMode && getItemImages(currentItem).length === 0) {
    alert("✔️ يجب رفع صورة واحدة على الأقل للوحة");
    return;
  }

  const calculatedItem = calculateItemPrice(currentItem, wallpaperRates);

  const updatedItems =
    editingItemIndex !== null
      ? invoice.items.map((it, idx) =>
          idx === editingItemIndex ? calculatedItem : it
        )
      : [...invoice.items, calculatedItem];

  const subtotal = updatedItems.reduce((sum, it) => sum + it.total, 0);

  setInvoice((prev) =>
    recalcInvoice({
      ...prev,
      items: updatedItems,
      subtotal,
    })
  );

  setCurrentItem(emptyItem);
  setEditingItemIndex(null);
  setShowItemForm(false);
};

  /* =============================
       7-ب) تعديل لوحة موجودة
     ============================= */
  const editItem = (index) => {
    setCurrentItem(invoice.items[index]);
    setEditingItemIndex(index);
    setShowItemForm(true);
  };

  /* =============================
       7-ج) نسخ لوحة موجودة (تكرار نفس التفاصيل بلوحة جديدة تُعدَّل بعدها)
     ============================= */
  const duplicateItem = (index) => {
    const original = invoice.items[index];
    if (!original) return;

    const duplicated = {
      ...original,
      panelImages: getItemImages(original).map((img) => ({
        ...img,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      })),
    };

    const newItems = [...invoice.items, duplicated];
    const subtotal = newItems.reduce((sum, it) => sum + it.total, 0);

    setInvoice((prev) =>
      recalcInvoice({
        ...prev,
        items: newItems,
        subtotal,
      })
    );

    // نفتح فورم التعديل على النسخة الجديدة مباشرة تسهيلاً لتعديل الفروقات
    setCurrentItem(duplicated);
    setEditingItemIndex(newItems.length - 1);
    setShowItemForm(true);
  };

  /* =============================
       8) حذف لوحة
     ============================= */
  const removeItem = (index) => {
    const newItems = invoice.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, it) => sum + it.total, 0);

   setInvoice((prev) =>
  recalcInvoice({
    ...prev,
    items: newItems,
    subtotal,
  })
);
  };

  /* =============================
       9) حفظ الفاتورة كـ Version جديد
       (مع حفظ JSON + صورة PNG)
     ============================= */
 // ✅ Phase 07-C2b: نفس whitelist الحقول العليا المستخدَمة بالباك اند
 // (POST /invoices و POST /invoices/:id/versions) - لبناء payload الحفظ.
 // invoiceNumber مستثنى عمداً: صار server-calculated بالكامل ولا يُرسل من
 // العميل إطلاقاً.
 const CREATE_INVOICE_FIELDS = [
   "customerName",
   "phone",
   "projectName",
   "companyName",
   "governorate",
   "nearestPoint",
   "date",
   "items",
   "subtotal",
   "consultationFee",
   "installationFee",
   "installationFeeManual",
   "transportFee",
   "transportFeeManual",
   "siteVisitFee",
   "hideTransportFeeFromCustomer",
   "notes",
   "discount",
   "partnerDiscountSource",
   "partnerDiscountPercent",
   "partnerDiscountAmount",
   "finalTotal",
   "paymentMethod",
   "advancePayment",
   "remainingPayment",
   "saleAccount",
   "panelTypeLabels",
   "sampleImages",
 ];

 const handleSaveInvoice = async () => {
  if (saving) return;

  // 📋 تسعيرة معتمدة صارت فاتورة مشروع: البنود جاية بدون تفاصيل - لازم تكتمل
  // (التسعيرة نفسها بدون تفاصيل أصلاً)
  if (projectInvoiceFlag && !isProjectMode && !quoteMode) {
    const missingIndex = invoice.items.findIndex(itemMissingDetails);
    if (missingIndex !== -1) {
      alert(
        `✔️ اللوحة رقم ${missingIndex + 1} ناقصة التفاصيل (شكل اللوحة، نوع ولون الإطار، الصور) - افتحيها من ✏️ وكمليها`
      );
      return;
    }
  }

  if (!invoice.customerName.trim()) {
    alert("✔️ يرجى إدخال اسم الزبون");
    return;
  }

  if (!invoice.phone) {
    alert("✔️ يرجى إدخال رقم الهاتف");
    return;
  }

  if (!invoice.invoiceNumber) {
    alert("✔️ يرجى إدخال رقم الفاتورة");
    return;
  }

  if (!invoice.governorate) {
    alert("✔️ يرجى اختيار المحافظة");
    return;
  }

  if (!invoice.nearestPoint.trim()) {
    alert("✔️ يرجى إدخال أقرب نقطة دالة");
    return;
  }

  if (!invoice.date) {
    alert("✔️ يرجى اختيار التاريخ");
    return;
  }

  if (isProjectInvoice && !String(invoice.projectName || "").trim()) {
    alert("✔️ يرجى إدخال اسم المشروع");
    return;
  }

  if (!invoice.items || invoice.items.length === 0) {
    alert("✔️ يجب إضافة لوحة واحدة على الأقل قبل الحفظ");
    return;
  }

  if (isProjectMode) {
    const invalidRowIndex = projectResults.rows.findIndex(
      (r) =>
        toNumber(r.lengthCm) <= 0 ||
        toNumber(r.widthCm) <= 0 ||
        toNumber(r.qty) <= 0 ||
        Math.round(r.projectPrice || 0) <= 0
    );
    if (invalidRowIndex !== -1) {
      alert(
        `✔️ بند المشروع رقم ${invalidRowIndex + 1}: يجب إدخال الطول والعرض والعدد وسعر البيع`
      );
      return;
    }

    // 📷 نفس شرط الفاتورة العادية: صورة واحدة على الأقل لكل لوحة
    const rowWithoutImageIndex = invoice.projectItems.findIndex(
      (row) => getItemImages(row).length === 0
    );
    if (rowWithoutImageIndex !== -1) {
      alert(
        `✔️ بند المشروع رقم ${rowWithoutImageIndex + 1}: يجب رفع صورة واحدة على الأقل للوحة`
      );
      return;
    }
  }

    try {
      setSaving(true);

      // 1) إنشاء صورة للفاتورة من المعاينة
      if (!previewRef.current) {
        alert("حدث خطأ أثناء تجهيز المعاينة");
        setSaving(false);
        return;
      }

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
      });

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      // ⚡ كل الرفوعات (صورة الفاتورة + صور اللوحات) تبدي بالخلفية بنفس اللحظة
      // وتشتغل بالتوازي ويه بناء ملف الـPDF - ننتظرها كلها بس قبل الحفظ.
      // (سابقاً كل خطوة تنتظر اللي قبلها: رفع ← تحويل كل صورة لـbase64 ← PDF ← رفع)
      const fileName = `invoice-${invoice.invoiceNumber}-${Date.now()}.png`;
      const storageRef = ref(storage, `invoices/${fileName}`);
      const imageUrlPromise = uploadBytes(storageRef, blob).then(() => getDownloadURL(storageRef));

      // 1-ب) رفع صور اللوحات (إذا موجودة) + تجهيز ملف PDF كامل:
      // صفحة 1 = صورة الفاتورة، وبعدها صفحة لكل لوحة فيها قياسها/عددها/نوع
      // ولون الإطار + صورتها. الصور تنقرأ للـPDF مباشرة من الملف المحلي (object
      // URL) أو من رابطها المحفوظ - بدون تحويلها لـbase64 كاملة أول
      const localImageUrls = [];
      const itemsWithImageData = (invoice.items || []).map((item) => {
        const { panelImageFile, panelImagePreview, panelImageUrl, panelImages, ...rest } = item;
        const images = getItemImages(item)
          .map((img) => {
            if (img.file) {
              // صورة جديدة اختارتها الموظفة الحين - ترتفع لـ Storage حتى تبقى
              // محفوظة مع النسخة
              const pdfSrc = URL.createObjectURL(img.file);
              localImageUrls.push(pdfSrc);
              const panelFileName = `invoice-${invoice.invoiceNumber}-panel-${Date.now()}-${img.file.name}`;
              const panelStorageRef = ref(storage, `invoices/panels/${panelFileName}`);
              const urlPromise = uploadBytes(panelStorageRef, img.file).then(() =>
                getDownloadURL(panelStorageRef)
              );
              return { pdfSrc, urlPromise };
            }
            if (img.url) {
              // صورة محفوظة من نسخة سابقة (عند التعديل)
              return { pdfSrc: img.url, urlPromise: Promise.resolve(img.url) };
            }
            return null;
          })
          .filter(Boolean);

        return { item: rest, images, imageDataUrls: images.map((img) => img.pdfSrc) };
      });

      let pdf;
      try {
        pdf = await buildMultiPagePdf(canvas, itemsWithImageData, invoice.panelTypeLabels);
      } finally {
        localImageUrls.forEach((url) => URL.revokeObjectURL(url));
      }

      const pdfBlob = pdf.output("blob");
      const pdfFileName = `invoice-${invoice.invoiceNumber}-${Date.now()}.pdf`;
      const pdfStorageRef = ref(storage, `invoices/${pdfFileName}`);
      const pdfUrlPromise = uploadBytes(pdfStorageRef, pdfBlob).then(() => getDownloadURL(pdfStorageRef));

      // روابط Storage لكل صور اللوحات + قياسها الحقيقي (لعرض سعر فاتورة المشروع)
      const uploadedItems = await Promise.all(
        itemsWithImageData.map(async ({ item, images }) => ({
          item,
          images: await Promise.all(
            images.map(async (img) => ({
              url: await img.urlPromise,
              size: decodedImageSizes.get(img.pdfSrc) || null,
            }))
          ),
        }))
      );
      const [imageUrl, pdfUrl] = await Promise.all([imageUrlPromise, pdfUrlPromise]);

      const cleanedItems = uploadedItems.map(({ item, images }) => ({
        ...item,
        panelImages: images.map((img) => ({ url: img.url })),
      }));
      const invoiceForSave = { ...invoice, items: cleanedItems };

      // 📋 التسعيرة: صور النماذج ترتفع وتنحفظ ويه النسخة نفسها
      if (quoteMode) {
        invoiceForSave.sampleImages = await Promise.all(
          sampleImages.map(async ({ file, url, width, height }) => {
            if (url) return { url, width, height };
            const safeName = file.name.replace(/[^\w.-]+/g, "_").slice(-80);
            const sampleRef = ref(
              storage,
              `invoices/samples/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`
            );
            const [size] = await Promise.all([readImageFileSize(file), uploadBytes(sampleRef, file)]);
            return { url: await getDownloadURL(sampleRef), ...size };
          })
        );
      }

      // 📐 فاتورة المشروع: بنود الفاتورة مبنية من بنود المشروع بنفس الترتيب
      // (applyProjectItemsToInvoice)، فنرجّع روابط الصور المرفوعة لكل بند
      // مشروع - بدل ملفات محلية/روابط preview مؤقتة ما تنحفظ
      if (isProjectMode) {
        invoiceForSave.projectItems = invoice.projectItems.map((row, i) => ({
          ...row,
          panelImages: cleanedItems[i]?.panelImages || [],
        }));
      }


      // 2) حفظ / تحديث وثيقة الفاتورة الرئيسية + النسخة الجديدة + العدّاد -
      // Phase 07-C2b: يستبدل الكتابات المباشرة الثلاث (addDoc invoices +
      // addDoc versions + setDoc merge على الفاتورة الأم + معاملة العدّاد
      // assignSequentialInvoiceNumber) بنداء واحد للباك اند. invoiceNumber
      // صار server-calculated بالكامل ولا يُقبل من العميل إطلاقاً - قرار
      // منتج صريح لمنع التلاعب/التكرار بتسلسل الفواتير. كمكسب إضافي: يزيل
      // خللاً كان موجوداً بالكود القديم - الكتابة الثانية (setDoc merge خطوة
      // 4 القديمة) كانت تعيد كتابة invoiceNumber من invoice.invoiceNumber
      // (قيمة state قديمة بنفس دورة التنفيذ، بسبب closure) بدل
      // finalInvoiceNumber الفعلي المحسوب حديثاً، فتلغي أثر معاملة العدّاد
      // بالضبط بسيناريو التسابق اللي صُمِّمت لمنعه. هنا الرقم يُقرأ فقط من
      // استجابة الباك اند، ما إله علاقة بـstate قديم.
      const invoicePayload = {};
      CREATE_INVOICE_FIELDS.forEach((key) => {
        if (invoiceForSave[key] !== undefined) {
          invoicePayload[key] = invoiceForSave[key];
        }
      });
      invoicePayload.imageUrl = imageUrl;
      invoicePayload.pdfUrl = pdfUrl;

      // 📐 فاتورة مشروع: البنود الخام + نتائج حسابها (سعر اللوحة/البيع/
      // المشروع) - الباك اند يحدّث منها المشروع المرتبط بقائمة المدير
      if (isProjectMode) {
        invoicePayload.projectItems = invoiceForSave.projectItems;
        invoicePayload.projectResults = computeProjectResults(
          invoiceForSave.projectItems
        );
      }

      // ✅ نقرأ الفاتورة "الفعلية الحالية" من activeInvoiceIdRef وليس من
      // invoiceId (رابط الصفحة) مباشرة - لتفادي اعتبار حفظة ثانية بنفس
      // الجلسة (بعد أول إنشاء ناجح، قبل أي navigate) "فاتورة جديدة".
      const effectiveInvoiceId = activeInvoiceIdRef.current;
      let newInvoiceId = effectiveInvoiceId;
      let finalInvoiceNumber;
      let newVersionId;
      let nextVersionNumber;

      if (!effectiveInvoiceId) {
        // فاتورة جديدة
        const createRes = await apiFetch(`${API_BASE}/invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isProjectInvoice
              ? { ...invoicePayload, invoiceSource: quoteMode ? "projectQuote" : "projectInvoice" }
              : invoicePayload
          ),
        });
        if (!createRes.ok) {
          throw new Error(await createRes.text().catch(() => ""));
        }
        const created = await createRes.json();
        newInvoiceId = created.id;
        finalInvoiceNumber = created.invoiceNumber;
        newVersionId = created.versionId;
        nextVersionNumber = created.versionNumber;

        // 🔒 تحديث فوري (بدون انتظار re-render) - أي ضغطة "حفظ" تالية بنفس
        // الجلسة الآن تُعامَل كنسخة جديدة لنفس الفاتورة، لا فاتورة مكررة.
        // (مزامنة رابط الصفحة نفسه تصير تحت - فقط إذا لم تختر الاعتماد الفوري
        // وتنتقل مباشرة لصفحة أخرى، لتفادي أي تضارب مع تحميل الفاتورة أثناء
        // نداء الاعتماد بالخلفية)
        activeInvoiceIdRef.current = newInvoiceId;
        if (isProjectInvoice) {
          setInvoiceCreatedBy(username);
        }
      } else {
        // فاتورة موجودة مسبقاً - نسخة جديدة
        const versionRes = await apiFetch(
          `${API_BASE}/invoices/${effectiveInvoiceId}/versions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(invoicePayload),
          }
        );
        if (!versionRes.ok) {
          throw new Error(await versionRes.text().catch(() => ""));
        }
        const createdVersion = await versionRes.json();
        newVersionId = createdVersion.versionId;
        nextVersionNumber = createdVersion.versionNumber;
        finalInvoiceNumber = createdVersion.invoiceNumber;
      }

      setInvoice({ ...invoiceForSave, invoiceNumber: finalInvoiceNumber });
      setCurrentVersionNumber(nextVersionNumber);

      alert(`✅ تم حفظ نسخة جديدة من الفاتورة (Version ${nextVersionNumber})`);

      // 📐 فاتورة مشروع: بدل سؤال الاعتماد والانتقال لإضافة الطلب، تروح لعرض
      // السعر - ينشأ/يتحدّث تلقائياً من الفاتورة (صور اللوحات + البنود
      // والرسوم). الاعتماد والتحويل لطلب صاروا من عرض السعر نفسه
      // (src/proposals/ProposalEditorPage.jsx) بنفس منطق الاعتماد بالضبط.
      if (goesToProposal) {
        try {
          const proposalId = await syncProposalWithInvoice({
            invoiceId: newInvoiceId,
            versionId: newVersionId,
            invoiceNumber: finalInvoiceNumber,
            invoice: invoiceForSave,
            samples: quoteMode
              ? invoiceForSave.sampleImages.map((img) => ({ src: img.url, width: img.width, height: img.height }))
              : undefined,
            priceItems: cleanedItems.map((item) =>
              buildProposalPriceItem(item, invoice.panelTypeLabels)
            ),
            images: uploadedItems.flatMap(({ images }) =>
              images.map((img) => ({ src: img.url, ...img.size }))
            ),
          });
          navigate(`/proposals/${proposalId}`);
        } catch (proposalErr) {
          console.error("Error preparing the invoice proposal:", proposalErr);
          alert(
            "⚠️ الفاتورة انحفظت، بس تعذّر تجهيز عرض السعر. احفظ الفاتورة مرة ثانية حتى يتجهز العرض."
          );
          if (!effectiveInvoiceId) {
            navigate(`/invoice/edit/${newInvoiceId}`, { replace: true });
          }
        }
        return;
      }

      // 🔔 اسأل فوراً إذا يريد اعتماد هذه النسخة والانتقال لإضافة الطلب
      const wantsApprove = window.confirm("هل تريد اعتماد هذه الفاتورة الآن؟");

      if (wantsApprove) {
        // ✅ Phase 07-C2a: يستبدل الكتابات المباشرة (اعتماد الفاتورة + النسخة
        // + قيدا كلفة الطباعة/الإطارات) بنداء واحد للباك اند - نفس الـendpoint
        // المستخدَم من InvoiceViewPage.jsx's handleApprove، ينهي الازدواج بين
        // الملفين ويجعل الاعتماد ذرياً (batch واحد بدل 4 كتابات منفصلة).
        const approveRes = await apiFetch(`${API_BASE}/invoices/${newInvoiceId}/approve`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ versionId: newVersionId }),
        });
        if (!approveRes.ok) {
          throw new Error(await approveRes.text().catch(() => ""));
        }

        // 👇 الانتقال لإضافة الطلب مع تعبئة البيانات تلقائياً
        const totalPanels = invoice.items?.reduce(
          (sum, item) => sum + Number(item.quantity || 0),
          0
        );

        navigate("/add", {
          state: {
            fromInvoice: true,
            customerName: invoice.customerName,
            governorate: invoice.governorate,
            price: invoice.finalTotal,
            note: `فاتورة رقم ${finalInvoiceNumber}`,
            boardsCount: totalPanels || 0,
            invoiceData: {
              id: newInvoiceId,
              customerName: invoice.customerName,
              invoiceNumber: finalInvoiceNumber,
            },
            invoiceImageUrl: imageUrl,
            invoicePdfUrl: pdfUrl,
            invoicePaymentMethod: invoice.paymentMethod || "cod",
            invoiceAdvancePayment: invoice.advancePayment ?? 0,
            saleAccount: invoice.saleAccount || "بيع",
            // 🤝 فاتورة ارت تاتش - الطلب ينضاف بمصدر "ارت تاتش" + رقم الزبون
            partnerSource,
            customerPhone: invoice.phone || "",
            // 🤝 خصم الشريك (ست سارة) - المصدر يتعبّى تلقائياً بالطلب
            partnerDiscountSource: invoice.partnerDiscountSource || "",
            partnerDiscountPercent: invoice.partnerDiscountPercent || 0,
            partnerDiscountAmount: invoice.partnerDiscountAmount || 0,
          },
        });
      } else if (!effectiveInvoiceId) {
        // ✅ أنشأنا فاتورة جديدة والموظفة قرّرت عدم اعتمادها فوراً - تبقى
        // بنفس الصفحة، فنزامن الرابط مع الفاتورة الفعلية (تعديل بدل إنشاء)
        // حتى لو حدّثت الصفحة أو رجعت لها لاحقاً. activeInvoiceIdRef محدَّث
        // مسبقاً فوق، فأي حفظ تالي بنفس الجلسة كان سيصير نسخة جديدة أصلاً
        // حتى بدون هذا الانتقال - هذا فقط لتصحيح الرابط المعروض بالمتصفح.
        navigate(`/invoice/edit/${newInvoiceId}`, { replace: true });
      }

      // خيار: ترجع لقائمة الفواتير
      // navigate("/invoices");
    } catch (err) {
      console.error("Error saving invoice:", err);
      alert("❌ حدث خطأ أثناء حفظ الفاتورة");
    } finally {
      setSaving(false);
    }
  };

  /* =============================
       10) تحميل الفاتورة كملف PDF (بدون حفظ بالسستم) - نفس تنسيق PDF
       الحفظ الفعلي (صورة الفاتورة + صفحة تفاصيل وصورة لكل لوحة)
     ============================= */
  const handleExportPdf = async () => {
    if (!previewRef.current || exportingPdf) return;

    try {
      setExportingPdf(true);

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
      });

      // نفس الحفظ: الصور تنقرأ مباشرة من الملف المحلي أو رابطها (بدون base64)
      const localImageUrls = [];
      const itemsWithImageData = (invoice.items || []).map((item) => ({
        item,
        imageDataUrls: getItemImages(item)
          .map((img) => {
            if (img.file) {
              const src = URL.createObjectURL(img.file);
              localImageUrls.push(src);
              return src;
            }
            return img.url || null;
          })
          .filter(Boolean),
      }));

      try {
        const pdf = await buildMultiPagePdf(canvas, itemsWithImageData, invoice.panelTypeLabels);
        pdf.save(buildSafeInvoiceFileName(invoice.customerName));
      } finally {
        localImageUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    } catch (err) {
      console.error("Error exporting invoice PDF:", err);
      alert("❌ حدث خطأ أثناء تجهيز ملف PDF");
    } finally {
      setExportingPdf(false);
    }
  };

const isAutoPrice =
  currentItem.sizeSource === "list" &&
  (currentItem.panelType === "أوريجن" || currentItem.panelType === "إدشن") &&
  !!currentItem.frameType &&
  currentItem.frameType !== "أخرى";

const availableSizeOptions = getFilteredSizeOptions(currentItem.panelShape);

  /* =============================
        UI — START
     ============================= */

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
        }}
      >
        جاري تحميل الفاتورة...
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f0f0f0",
        padding: "20px",
      }}
    >
      {/* شريط علوي بسيط */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
          alignItems: "center",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #ccc",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          ← رجوع
        </button>

        <div style={{ textAlign: "center", flex: 1 }}>
         <h2
  style={{
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
  }}
>
  {versionId
    ? "✏️ تعديل النسخة (فتح نسخة محددة)"
    : invoiceId
    ? "✏️ تعديل الفاتورة (آخر نسخة)"
    : isProjectInvoice
    ? "🏗️ إنشاء فاتورة مشروع"
    : "🧾 إنشاء فاتورة أرتيكا"}
</h2>
          {isProjectInvoice && (invoiceId || activeInvoiceIdRef.current) && (
            <span
              style={{
                display: "inline-block",
                marginTop: 6,
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: 20,
                background: "#ede7f6",
                color: "#4527a0",
              }}
            >
              {getProjectInvoiceLabel(invoiceCreatedBy || username)}
            </span>
          )}
          {invoiceId && currentVersionNumber && (
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              رقم الفاتورة: {invoice.invoiceNumber} — آخر نسخة: V
              {currentVersionNumber}
            </div>
          )}
        </div>

        <div style={{ width: 80 }} /> {/* مسافة عوض زر الرجوع */}
      </div>

      {/* المحتوى داخل صندوق كبير */}
      <div
        style={{
          background: "#fff",
          width: "100%",
          height: "85vh",
          borderRadius: "12px",
          display: "flex",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        {/* ======================
              Right Form
           ====================== */}
        <div
          style={{
            width: "38%",
            borderLeft: "1px solid #ddd",
            padding: "20px",
            overflowY: "auto",
          }}
        >
          {/* 📐 نوع الفاتورة: عادية أو مشروع - البوكس يظهر فقط عند الإنشاء
              (فاتورة موجودة تبقى على نوعها) ولبنين/المدير فقط */}
          {!invoiceId && !activeInvoiceIdRef.current && canCreateProject && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: isProjectInvoice ? "#ede7f6" : "#fff",
                padding: "14px 18px",
                borderRadius: "10px",
                border: `1px solid ${isProjectInvoice ? "#b39ddb" : "#ddd"}`,
                marginBottom: 20,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={isProjectInvoice}
                onChange={handleToggleProjectMode}
                style={{ width: 18, height: 18 }}
              />
              🏗️ فاتورة مشروع
              <span style={{ fontSize: 12, color: "#666", fontWeight: 400 }}>
                (نفس الفاتورة العادية، واسم المشروع إجباري)
              </span>
            </label>
          )}

          {/* 📋 تسعيرة فقط - مشروع جديد (مو تحويل تسعيرة ولا تعديلها) */}
          {!invoiceId && !activeInvoiceIdRef.current && canCreateProject && projectInvoiceFlag && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: quoteMode ? "#fff8e1" : "#fff",
                padding: "14px 18px",
                borderRadius: "10px",
                border: `1px solid ${quoteMode ? "#ffcc80" : "#ddd"}`,
                marginBottom: 20,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={quoteMode}
                onChange={(e) => setQuoteMode(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              📋 تسعيرة فقط
              <span style={{ fontSize: 12, color: "#666", fontWeight: 400 }}>
                (بدون شكل اللوحة والإطار والصور - اعتمادها من عروض الأسعار يحوّلها لفاتورة مشروع)
              </span>
            </label>
          )}

          {quoteMode && (invoiceId || activeInvoiceIdRef.current) && (
            <div style={{ background: "#fff8e1", border: "1px solid #ffcc80", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontWeight: 600 }}>
              📋 تسعيرة - الاعتماد من عروض الأسعار (يحوّلها لفاتورة مشروع)
            </div>
          )}

          {projectInvoiceFlag && !quoteMode && invoice.items.some(itemMissingDetails) && (
            <div style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 10, padding: "12px 16px", marginBottom: 20, lineHeight: 1.7 }}>
              <b>✅ التسعيرة انعتمدت - كملي تفاصيل اللوحات</b>
              <div style={{ fontSize: 13 }}>
                افتحي كل لوحة (✏️ تعديل) وكملي: شكل اللوحة، نوع ولون الإطار، والصور - وبعدين احفظي.
                <span style={{ color: "#c62828" }}>
                  {" "}
                  باقي {invoice.items.filter(itemMissingDetails).length} لوحة ناقصة التفاصيل.
                </span>
              </div>
            </div>
          )}

          {quoteMode && (
            <div style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🖼️ صور نماذج</div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
                صور نماذج للزبون (مو مربوطة بلوحة معيّنة) - تطلع بعرض السعر كصفحات «اللوحات المقترحة»
              </div>
              <label style={{ display: "inline-block", padding: "8px 16px", borderRadius: 6, border: "1px solid #007bff", color: "#007bff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                📤 إضافة صور نماذج
                <input type="file" accept="image/*" multiple onChange={handleSampleImagesChange} style={{ display: "none" }} />
              </label>
              {sampleImages.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  {sampleImages.map((img) => (
                    <div key={img.id} style={{ position: "relative" }}>
                      <img src={img.preview || img.url} alt="نموذج" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} />
                      <button
                        type="button"
                        title="حذف الصورة"
                        onClick={() => setSampleImages((prev) => prev.filter((x) => x.id !== img.id))}
                        style={{ position: "absolute", top: -6, left: -6, width: 22, height: 22, borderRadius: "50%", border: "none", background: "#dc3545", color: "#fff", fontSize: 12, lineHeight: "22px", cursor: "pointer" }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* معلومات الزبون */}
          <div
            style={{
              background: "#fff",
              padding: "18px 22px",
              borderRadius: "10px",
              border: "1px solid #ddd",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                textAlign: "right",
                marginBottom: 18,
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              معلومات الزبون
            </h3>

            {/* ✅ الحقول الإلزامية أولاً (اسم الزبون/الهاتف/المحافظة/أقرب
                نقطة دالة - نفس الحقول التي يمنع handleSaveInvoice الحفظ بدونها)،
                وبعدها الحقول الاختيارية بالأسفل - بناءً على طلب صريح لتسهيل
                تعبئة النموذج بالترتيب الصحيح */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label>اسم الزبون</label>
                <input
                  type="text"
                  name="customerName"
                  style={inputStyle}
                  value={invoice.customerName}
                  required
                  onChange={handleHeaderChange}
                />
              </div>
{/* PHONE */}
<div>
  <label style={{ fontSize: 13, color: "#555" }}>
    رقم الهاتف (إلزامي)
  </label>
  <input
    type="number"
    name="phone"
    style={inputStyle}
    value={invoice.phone}
    required
    onChange={handleHeaderChange}
  />
</div>

              <div>
                <label>المحافظة</label>
                <select
                  name="governorate"
                  style={inputStyle}
                  value={invoice.governorate}
                  required
                  onChange={handleHeaderChange}
                >
                  <option value="">اختر المحافظة</option>
                  <option value="بغداد">بغداد</option>
                  <option value="البصرة">البصرة</option>
                  <option value="نينوى">نينوى</option>
                  <option value="أربيل">أربيل</option>
                  <option value="السليمانية">السليمانية</option>
                  <option value="دهوك">دهوك</option>
                  <option value="الأنبار">الأنبار</option>
                  <option value="كربلاء">كربلاء</option>
                  <option value="النجف">النجف</option>
                  <option value="بابل">بابل</option>
                  <option value="واسط">واسط</option>
                  <option value="ميسان">ميسان</option>
                  <option value="ذي قار">ذي قار</option>
                  <option value="الديوانية">الديوانية (القادسية)</option>
                  <option value="صلاح الدين">صلاح الدين</option>
                  <option value="ديالى">ديالى</option>
                  <option value="كركوك">كركوك</option>
                  <option value="المثنى">المثنى</option>
                  <option value="حلبجة">حلبچة</option>
                </select>
              </div>

              <div>
                <label>أقرب نقطة دالة</label>
                <input
                  type="text"
                  name="nearestPoint"
                  style={inputStyle}
                  value={invoice.nearestPoint}
                  required
                  onChange={handleHeaderChange}
                />
              </div>

              <div
                style={{
                  borderTop: "1px dashed #ddd",
                  paddingTop: 12,
                  marginTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div>
                  <label>
                    {isProjectInvoice ? (
                      <>
                        اسم المشروع <span style={{ color: "#c62828" }}>*</span>
                      </>
                    ) : (
                      "اسم المشروع (اختياري)"
                    )}
                  </label>
                  <input
                    type="text"
                    name="projectName"
                    required={isProjectInvoice}
                    style={inputStyle}
                    value={invoice.projectName}
                    onChange={handleHeaderChange}
                  />
                </div>
{/* COMPANY NAME */}
<div>
  <label style={{ fontSize: 13, color: "#555" }}>
    اسم الشركة (اختياري)
  </label>
  <input
    type="text"
    name="companyName"
    style={inputStyle}
    value={invoice.companyName}
    onChange={handleHeaderChange}
  />
</div>
                {/* ✅ حقل رقم الفاتورة أُخفي من نموذج الإدخال بناءً على طلب
                    صريح: الرقم يتولد تلقائياً من السيرفر عند الحفظ وكان أصلاً
                    readOnly هنا (Phase 07-D) - عرضه بشكل input كان يوهم
                    الموظفة أنه قابل للتعديل. invoice.invoiceNumber نفسه لم
                    يُمس (لا يزال يُولَّد بـgenerateInvoiceNumber عند الإنشاء
                    ويُستخدم بالحفظ/الفاتورة المطبوعة بالضبط كالسابق) - فقط
                    الحقل بالنموذج اختفى. يبقى ظاهراً برأس الصفحة عند التعديل
                    (أعلى) وعلى الفاتورة نفسها بالمعاينة/الطباعة (تحت). */}

                {showDateField ? (
                  <div>
                    <label>التاريخ</label>
                    <input
                      type="date"
                      name="date"
                      style={inputStyle}
                      value={invoice.date}
                      onChange={handleHeaderChange}
                    />
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowDateField(true)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#1976d2",
                        fontSize: 13,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      تغيير التاريخ (افتراضياً تاريخ اليوم)
                    </button>
                  </div>
                )}

                {/* 📐 فاتورة المشروع بدون "حساب البيع" (طلب المدير) - تبقى
                    قيمتها المحفوظة ("بيع" افتراضياً) حتى الطلب والمحاسبة
                    ما يتأثرون */}
                {!isProjectInvoice && (
                <div>
                  <label>حساب البيع (داخلي - لا يظهر للزبون)</label>
                  <select
                    name="saleAccount"
                    style={inputStyle}
                    value={invoice.saleAccount || "بيع"}
                    onChange={handleHeaderChange}
                  >
                    <option value="بيع">بيع</option>
                    <option value="حساب استاذ مروان">حساب استاذ مروان</option>
                    <option value="حساب نرسي">حساب نرسي</option>
                    <option value="مطبعة صدى سومر - أبو زينب (زبون)">مطبعة صدى سومر - أبو زينب (زبون)</option>
                    <option value="ماركتنك">ماركتنك</option>
                  </select>
                </div>
                )}
              </div>
            </div>
          </div>

          {/* 📐 بنود المشروع - بدل "إضافة لوحة" بفاتورة المشروع. الموظفة
              تشوف سعر البيع والمجموع فقط - سعر اللوحة وسعر المشروع والمعامل
              تظهر للمدير بواجهة حساب المشروع */}
          {isProjectMode && (
            <div
              style={{
                background: "#fff",
                padding: "18px 22px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                marginBottom: 20,
              }}
            >
              <h3
                style={{
                  textAlign: "right",
                  marginBottom: 18,
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                📐 بنود المشروع
              </h3>

              <div style={{ overflowX: "auto" }}>
                <table style={projectTableStyle}>
                  <thead>
                    <tr>
                      <th style={projectThStyle}>الطول</th>
                      <th style={projectThStyle}>العرض</th>
                      <th style={projectThStyle}>نوع اللوحة</th>
                      <th style={projectThStyle}>نوع الإطار</th>
                      <th style={projectThStyle}>العدد</th>
                      <th style={projectThStyle}>سعر البيع</th>
                      <th style={projectThStyle}>المجموع</th>
                      <th style={projectThStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.projectItems.map((it, idx) => {
                      const row = projectResults.rows[idx];
                      const rowImages = getItemImages(it);
                      return (
                        <React.Fragment key={idx}>
                        <tr>
                          <td style={projectTdStyle}>
                            <input
                              type="number"
                              min="0"
                              style={projectInputStyle}
                              value={it.lengthCm}
                              onChange={(e) =>
                                updateProjectRow(idx, "lengthCm", e.target.value)
                              }
                            />
                          </td>
                          <td style={projectTdStyle}>
                            <input
                              type="number"
                              min="0"
                              style={projectInputStyle}
                              value={it.widthCm}
                              onChange={(e) =>
                                updateProjectRow(idx, "widthCm", e.target.value)
                              }
                            />
                          </td>
                          <td style={projectTdStyle}>
                            <select
                              style={projectInputStyle}
                              value={it.drawingType}
                              onChange={(e) =>
                                updateProjectRow(idx, "drawingType", e.target.value)
                              }
                            >
                              <option value="origin">اورجن</option>
                              <option value="edition">ادشن</option>
                            </select>
                          </td>
                          <td style={projectTdStyle}>
                            <select
                              style={{ ...projectInputStyle, minWidth: 120 }}
                              value={it.boardType}
                              onChange={(e) =>
                                updateProjectRow(idx, "boardType", e.target.value)
                              }
                            >
                              <option value="print_only">خشب داخلي فقط</option>
                              <option value="print_ail">إطار أيل + داخلي</option>
                              <option value="print_kas">إطار كاس</option>
                            </select>
                          </td>
                          <td style={projectTdStyle}>
                            <input
                              type="number"
                              min="1"
                              style={projectInputStyle}
                              value={it.qty}
                              onChange={(e) =>
                                updateProjectRow(idx, "qty", e.target.value)
                              }
                            />
                          </td>
                          <td style={projectTdStyle}>
                            <input
                              type="number"
                              min="0"
                              style={{
                                ...projectInputStyle,
                                minWidth: 95,
                                fontWeight: 700,
                                borderColor: "#1976d2",
                              }}
                              value={Math.round(row?.projectPrice || 0)}
                              onChange={(e) =>
                                updateProjectRowPrice(idx, e.target.value)
                              }
                            />
                          </td>
                          <td
                            style={{
                              ...projectTdStyle,
                              fontWeight: 700,
                              background: "#fafafa",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {Math.round(row?.projectTotal || 0).toLocaleString()}
                          </td>
                          <td style={projectTdStyle}>
                            <button
                              type="button"
                              onClick={() => removeProjectRow(idx)}
                              title="حذف البند"
                              style={{
                                background: "#fff1f1",
                                border: "1px solid #ffcdd2",
                                padding: "4px 6px",
                                borderRadius: 6,
                                color: "#d32f2f",
                                cursor: "pointer",
                              }}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                        {/* 📷 صور البند (إلزامية - صورة وحدة على الأقل) */}
                        <tr>
                          <td
                            colSpan={8}
                            style={{
                              ...projectTdStyle,
                              textAlign: "right",
                              background: rowImages.length ? "#fff" : "#fffaf0",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#555" }}>
                                📷 صور البند {idx + 1}:
                              </span>
                              {rowImages.map((img, imgIdx) => (
                                <div
                                  key={img.id || img.url || imgIdx}
                                  style={{ position: "relative", width: 52, height: 52 }}
                                >
                                  <img
                                    src={img.preview || img.url}
                                    alt={`صورة ${imgIdx + 1}`}
                                    style={{
                                      width: 52,
                                      height: 52,
                                      objectFit: "cover",
                                      borderRadius: 6,
                                      border: "1px solid #ddd",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    title="حذف الصورة"
                                    onClick={() => removeProjectRowImage(idx, imgIdx)}
                                    style={{
                                      position: "absolute",
                                      top: -6,
                                      left: -6,
                                      width: 18,
                                      height: 18,
                                      borderRadius: "50%",
                                      border: "none",
                                      background: "#d32f2f",
                                      color: "#fff",
                                      fontSize: 11,
                                      lineHeight: "18px",
                                      padding: 0,
                                      cursor: "pointer",
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <label
                                style={{
                                  fontSize: 12,
                                  color: "#1976d2",
                                  border: "1px dashed #1976d2",
                                  borderRadius: 6,
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                }}
                              >
                                {rowImages.length ? "➕ صورة أخرى" : "➕ رفع صورة (إلزامي)"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  style={{ display: "none" }}
                                  onChange={(e) => addProjectRowImages(idx, e)}
                                />
                              </label>
                            </div>
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={addProjectRow}
                style={{
                  width: "100%",
                  padding: 10,
                  marginTop: 12,
                  border: "1px dashed #1976d2",
                  background: "none",
                  color: "#1976d2",
                  fontWeight: 700,
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                + سطر جديد
              </button>

              <div
                style={{
                  marginTop: 12,
                  fontWeight: 700,
                  color: "#ef6c00",
                  textAlign: "right",
                }}
              >
                إجمالي سعر البيع: {Number(invoice.subtotal || 0).toLocaleString()} د.ع
              </div>
            </div>
          )}

          {/* إضافة لوحة */}
          {!isProjectMode && (
          <div
            style={{
              background: "#fff",
              padding: "18px 22px",
              borderRadius: "10px",
              border: "1px solid #ddd",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                textAlign: "right",
                marginBottom: 18,
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              إضافة لوحة
            </h3>

            <button
              type="button"
              onClick={() => {
                if (showItemForm) {
                  setShowItemForm(false);
                } else {
                  // ✅ قيم ديفولت عند فتح "لوحة جديدة" بناءً على طلب صريح
                  // (أغلب اللوحات فعلياً إطار كامل + L-Frame) - تبقى قابلة
                  // للتغيير بالكامل من نفس الحقول كالسابق
                  setCurrentItem({
                    ...emptyItem,
                    frameOption: "إطار كامل",
                    frameType: "L",
                  });
                  setEditingItemIndex(null);
                  setShowItemForm(true);
                }
              }}
              style={{
                marginBottom: 14,
                background: "#fff",
                border: "1px solid #777",
                padding: "6px 12px",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {showItemForm ? "إخفاء الفورم" : "➕ لوحة جديدة"}
            </button>

            {showItemForm && (
              <div
                style={{
                  marginTop: 8,
                  padding: 14,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  background: "#fafafa",
                }}
              >
                <div style={{ marginBottom: 10 }}>
                  <label>اسم اللوحة</label>
                  <input
                    type="text"
                    name="panelName"
                    style={inputStyle}
                    value={currentItem.panelName}
                    onChange={handleItemChange}
                    required
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label>نوع اللوحة</label>
                  <select
                    name="panelType"
                    style={inputStyle}
                    value={currentItem.panelType}
                    onChange={handleItemChange}
                    required
                  >
                    <option value="">اختر النوع</option>
                    {PANEL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
{!quoteMode && !isWallpaper(currentItem) && (
<div style={{ marginBottom: 10 }}>
  <label>شكل اللوحة</label>
  <select
    name="panelShape"
    style={inputStyle}
    value={currentItem.panelShape}
    onChange={handleItemChange}
    required
  >
    <option value="">اختر شكل اللوحة</option>
    {PANEL_SHAPES.map((shape) => (
      <option key={shape} value={shape}>
        {shape}
      </option>
    ))}
  </select>
</div>
)}
                {isWallpaper(currentItem) && (
                  <div style={{ marginBottom: 10 }}>
                    <label>التركيب</label>
                    <select
                      name="wallpaperInstall"
                      style={inputStyle}
                      value={currentItem.wallpaperInstall === false ? "without" : "with"}
                      onChange={handleItemChange}
                    >
                      <option value="with">مع تركيب</option>
                      <option value="without">بدون تركيب</option>
                    </select>
                    <small style={{ color: "#777" }}>
                      ورق الجدران بدون إطار - السعر = المساحة × سعر المتر (
                      {wallpaperRateFor(currentItem, wallpaperRates).toLocaleString()} دينار/م²)
                    </small>
                  </div>
                )}

                {!isWallpaper(currentItem) && (
                <div style={{ marginBottom: 10 }}>
                  <label>الإطار؟</label>
                  <select
                    name="frameOption"
                    style={inputStyle}
                    value={currentItem.frameOption}
                    onChange={handleItemChange}
                    required
                  >
                    <option value="">اختر خيار الإطار</option>
                    {FRAME_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                )}

                {!quoteMode && currentItem.frameOption === "إطار كامل" && (
                  <div style={{ marginBottom: 10 }}>
                    <label>نوع الإطار</label>
                    <select
                      name="frameType"
                      style={inputStyle}
                      value={currentItem.frameType}
                      onChange={handleItemChange}
                      required
                    >
                      <option value="">اختر نوع الإطار</option>
                      {FRAME_TYPES.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!quoteMode && currentItem.frameOption === "إطار كامل" &&
                  currentItem.frameType === "أخرى" && (
                    <div style={{ marginBottom: 10 }}>
                      <label>اكتب نوع الإطار</label>
                      <input
                        type="text"
                        name="customFrameType"
                        style={inputStyle}
                        value={currentItem.customFrameType}
                        onChange={handleItemChange}
                        placeholder="اكتب نوع الإطار"
                        required
                      />
                    </div>
                  )}
{!quoteMode && currentItem.frameOption === "إطار كامل" && currentItem.frameType && (
  <div style={{ marginBottom: 10 }}>
    <label>لون الإطار</label>
    <select
      name="frameColor"
      style={inputStyle}
      value={currentItem.frameColor}
      onChange={handleItemChange}
      required
    >
      <option value="">اختر لون الإطار</option>

      {(FRAME_COLORS[currentItem.frameType] || []).map((color) => (
        <option key={color} value={color}>
          {color}
        </option>
      ))}

      <option value="أخرى">أخرى</option>
    </select>
  </div>
)}

{!quoteMode && currentItem.frameOption === "إطار كامل" &&
  currentItem.frameType &&
  currentItem.frameColor === "أخرى" && (
    <div style={{ marginBottom: 10 }}>
      <label>اكتب لون الإطار</label>
      <input
        type="text"
        name="customFrameColor"
        style={inputStyle}
        value={currentItem.customFrameColor}
        onChange={handleItemChange}
        placeholder="اكتب لون الإطار"
        required
      />
    </div>
  )}
                {/* القياس */}
                <div style={{ marginBottom: 10 }}>
                  <label>{isWallpaper(currentItem) ? "قياس ورق الجدران (الطول × العرض)" : "القياس"}</label>
                  {!isWallpaper(currentItem) && (
                  <select
                    name="sizeKey"
                    style={inputStyle}
                    value={
                      currentItem.sizeSource === "list"
                        ? currentItem.sizeKey
                        : "custom"
                    }
                    onChange={handleItemChange}
                    required
                  >
                    <option value="">اختر القياس</option>
                   {availableSizeOptions.map((size) => (
  <option key={size} value={size}>
    {size.replace("x", "×")}
  </option>
))}
                    <option value="custom">أخرى (قياس يدوي)</option>
                  </select>
                  )}

                  {currentItem.sizeSource === "custom" && (
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 10,
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <label>الطول (سم)</label>
                        <input
                          type="number"
                          min="1"
                          style={inputStyle}
                          value={currentItem.customHeight}
                          required
                          onChange={(e) =>
                            handleCustomSizeChange(
                              "customHeight",
                              e.target.value
                            )

                          }
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>العرض (سم)</label>
                        <input
                          type="number"
                          min="1"
                          style={inputStyle}
                          value={currentItem.customWidth}
                          onChange={(e) =>
                            handleCustomSizeChange(
                              "customWidth",
                              e.target.value
                            )
                          }
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label>العدد</label>
                  <input
                    type="number"
                    name="quantity"
                    min={1}
                    style={inputStyle}
                    value={currentItem.quantity}
                    required
                    onChange={handleItemChange}
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label>سعر اللوحة (دينار)</label>
               <input
  type="number"
  name="pricePerPanel"
  style={inputStyle}
  value={currentItem.pricePerPanel}
  required
  onChange={handleItemChange}
/>
                  {isAutoPrice && (
                    <small style={{ color: "#777" }}>
                      السعر محسوب تلقائيًا حسب جدول الأسعار.
                    </small>
                  )}
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label>المجموع لهذا السطر</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={currentItem.total}
                    readOnly
                  />
                </div>

                {!quoteMode && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: "block", marginBottom: 6 }}>
                    📷 صور اللوحة (إلزامي - يمكن إضافة أكثر من صورة لنفس اللوحة، تظهر كلها مدمجة بصفحة واحدة بملف الفاتورة PDF)
                  </label>
                  <label
                    style={{
                      display: "inline-block",
                      padding: "8px 16px",
                      borderRadius: 6,
                      border: "1px solid #007bff",
                      color: "#007bff",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {getItemImages(currentItem).length ? "📤 إضافة صورة أخرى" : "📤 اختيار صورة/صور اللوحة"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePanelImagesChange}
                      style={{ display: "none" }}
                    />
                  </label>
                  {getItemImages(currentItem).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                      {getItemImages(currentItem).map((img) => (
                        <div key={img.id} style={{ position: "relative" }}>
                          <img
                            src={img.preview || img.url}
                            alt="معاينة صورة اللوحة"
                            style={{
                              width: 110,
                              height: 110,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ddd",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removePanelImage(img.id)}
                            title="حذف الصورة"
                            style={{
                              position: "absolute",
                              top: -6,
                              left: -6,
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              border: "none",
                              background: "#dc3545",
                              color: "#fff",
                              fontSize: 12,
                              lineHeight: "22px",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                    marginTop: 10,
                  }}
                >
                  <button
                    type="button"
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      background: "#ccc",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setShowItemForm(false);
                      setCurrentItem(emptyItem);
                      setEditingItemIndex(null);
                    }}
                  >
                    إلغاء
                  </button>

                  <button
                    type="button"
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      background: "#007bff",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={addItem}
                  >
                    {editingItemIndex !== null ? "تحديث اللوحة" : "حفظ اللوحة"}
                  </button>
                </div>
              </div>
            )}

            {/* قائمة اللوحات المضافة (اختياري تبين هنا) */}
            {invoice.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ textAlign: "right" }}>اللوحات الحالية</h4>
                <ul style={{ paddingRight: 18 }}>
                  {invoice.items.map((it, idx) => (
                    <li
                      key={idx}
                      style={{
                        fontSize: 13,
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <span>
                        {it.panelName} — {it.sizeKey} — {it.quantity} حبة
                        {getItemImages(it).length > 0 &&
                          ` — 📷 مرفقة ${getItemImages(it).length > 1 ? `${getItemImages(it).length} صور` : "صورة"}`}
                      </span>
                      <span style={{ display: "flex", gap: 10 }}>
                        <button
                          type="button"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#007bff",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                          onClick={() => editItem(idx)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          title="نسخ نفس تفاصيل اللوحة للوحة جديدة يمكن تعديلها"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#28a745",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                          onClick={() => duplicateItem(idx)}
                        >
                          نسخ
                        </button>
                        <button
                          type="button"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "red",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                          onClick={() => {
                            if (editingItemIndex === idx) {
                              setCurrentItem(emptyItem);
                              setEditingItemIndex(null);
                              setShowItemForm(false);
                            }
                            removeItem(idx);
                          }}
                        >
                          حذف
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          )}

          {/* المجاميع */}
          <div
            style={{
              background: "#fff",
              padding: "18px 22px",
              borderRadius: "10px",
              border: "1px solid #ddd",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                textAlign: "right",
                marginBottom: 18,
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              المجاميع
            </h3>

            <div style={{ marginBottom: 10 }}>
              <label>المجموع الأولي (من اللوحات)</label>
              <input
                type="number"
                style={inputStyle}
                value={invoice.subtotal}
                readOnly
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label>رسوم التصميم والاستشارة الهندسية</label>
              <input
                type="number"
                name="consultationFee"
                style={inputStyle}
                value={invoice.consultationFee}
                onChange={handleHeaderChange}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label>أجور النقل</label>
              <input
                type="number"
                name="transportFee"
                style={inputStyle}
              value={
  invoice.transportFeeManual !== undefined
    ? invoice.transportFeeManual
    : invoice.transportFee
}
                onChange={handleHeaderChange}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!invoice.hideTransportFeeFromCustomer}
                  onChange={(e) =>
                    setInvoice((prev) => ({
                      ...prev,
                      hideTransportFeeFromCustomer: e.target.checked,
                    }))
                  }
                />
                إخفاء أجور النقل عن الزبون (يبقى ضمن المجموع الكلي)
              </label>
            </div>
<div style={{ marginBottom: 10 }}>
  <label>أجور التركيب</label>
  <input
    type="number"
    name="installationFee"
    style={inputStyle}
   value={
  invoice.installationFeeManual !== undefined
    ? invoice.installationFeeManual
    : invoice.installationFee
}
    onChange={handleHeaderChange}
  />
</div>

<div style={{ marginBottom: 10 }}>
  <label>زيارة الموقع</label>
  <input
    type="number"
    name="siteVisitFee"
    style={inputStyle}
    value={invoice.siteVisitFee}
    onChange={handleHeaderChange}
  />
</div>
            {/* 🤝 طلب عن طريق شريك (ست سارة) - خصم نسبة على اللوحات فقط */}
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={invoice.partnerDiscountSource === "ست سارة"}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setInvoice((prev) =>
                      recalcInvoice({
                        ...prev,
                        partnerDiscountSource: checked ? "ست سارة" : "",
                        partnerDiscountPercent: checked ? DEFAULT_PARTNER_DISCOUNT_PERCENT : 0,
                      })
                    );
                  }}
                />
                طلب عن طريق ست سارة
              </label>
              {invoice.partnerDiscountSource && (
                <div style={{ marginTop: 6 }}>
                  <label>نسبة خصم الشريك % (على اللوحات فقط)</label>
                  <input
                    type="number"
                    name="partnerDiscountPercent"
                    min={0}
                    max={99}
                    style={inputStyle}
                    value={invoice.partnerDiscountPercent}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || (Number(v) >= 0 && Number(v) < 100)) {
                        handleHeaderChange(e);
                      }
                    }}
                  />
                  <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
                    قيمة الخصم: {Number(invoice.partnerDiscountAmount || 0).toLocaleString()} د.ع
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label>الخصم</label>
              <input
                type="number"
                name="discount"
                style={inputStyle}
                value={invoice.discount}
                onChange={handleHeaderChange}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: "bold" }}>تسميات نوع اللوحة بالفاتورة</label>
              <div style={{ fontSize: 12, color: "#777", margin: "4px 0 8px" }}>
                هذا النص يظهر للزبون بدل اسم النوع (أوريجن / إدشن / برايم) - تكدرين تعدلينه لهذه الفاتورة فقط
              </div>
              {PANEL_TYPES.map((type) => (
                <div key={type} style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12, color: "#555" }}>{type}</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={invoice.panelTypeLabels?.[type] ?? PANEL_TYPE_INVOICE_LABELS[type]}
                    onChange={(e) =>
                      setInvoice((prev) => ({
                        ...prev,
                        panelTypeLabels: {
                          ...prev.panelTypeLabels,
                          [type]: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label>ملاحظات (اختياري)</label>
              <textarea
                name="notes"
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
                placeholder="أي ملاحظات أو اتفاق خاص مع الزبون..."
                value={invoice.notes || ""}
                onChange={handleHeaderChange}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label>المجموع النهائي</label>
              <input
                type="text"
                style={inputStyle}
                value={invoice.finalTotal.toLocaleString()}
                readOnly
              />
            </div>
<div style={{ marginBottom: 10 }}>
<label>طريقة الدفع</label>
<select
  name="paymentMethod"
  style={inputStyle}
  value={invoice.paymentMethod}
  onChange={handleHeaderChange}
>
  <option value="cod">دفع عند الاستلام</option>
  <option value="partial">دفعة أولية</option>
  <option value="full">دفعة كاملة اونلاين</option>
</select>
</div>

<div style={{ marginBottom: 10 }}>
 {invoice.paymentMethod !== "cod" && (
  <div style={{ marginBottom: 10 }}>
    <label>الدفعة المقدمة</label>
    <input
      type="number"
      name="advancePayment"
      style={inputStyle}
      value={invoice.advancePayment}
      onChange={handleHeaderChange}
      readOnly={invoice.paymentMethod === "full"}
    />
  </div>
)}
</div>

<div style={{ marginBottom: 10 }}>
  <label>المبلغ المتبقي</label>
  <input
    type="text"
    style={inputStyle}
    value={(invoice.remainingPayment || 0).toLocaleString()}
    readOnly
  />
</div>
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  background: "#6c757d",
                  color: "#fff",
                  border: "none",
                  cursor: exportingPdf ? "not-allowed" : "pointer",
                  opacity: exportingPdf ? 0.7 : 1,
                }}
                onClick={handleExportPdf}
                disabled={exportingPdf}
              >
                {exportingPdf ? "⏳ جارٍ تجهيز PDF..." : "💾 تحميل الفاتورة PDF"}
              </button>

              <button
                type="button"
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  minWidth: 140,
                  opacity: saving ? 0.7 : 1,
                }}
                onClick={handleSaveInvoice}
                disabled={saving}
              >
                {saving
                  ? "جارٍ الحفظ..."
                  : quoteMode
                  ? "حفظ التسعيرة والانتقال لعرض السعر"
                  : goesToProposal
                  ? "حفظ كنسخة جديدة والانتقال لعرض السعر"
                  : "حفظ الفاتورة كنسخة جديدة"}
              </button>
            </div>
          </div>
        </div>

        {/* ======================
              LEFT — INVOICE PREVIEW
           ====================== */}
        <div
          style={{
            flex: 1,
            padding: 16,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f4f4f4",
          }}
        >
          <InvoicePreview ref={previewRef} invoice={invoice} />
        </div>
      </div>
    </div>
  );
};

/* =============================
     CSS Styles (Used Above)
     ============================= */
const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: 13,
  marginTop: 4,
};

// 📐 جدول بنود فاتورة المشروع
const projectTableStyle = {
  width: "100%",
  minWidth: 640,
  borderCollapse: "collapse",
  fontSize: 13,
};

const projectThStyle = {
  padding: "8px 4px",
  background: "#f5f5f5",
  border: "1px solid #e0e0e0",
  fontWeight: 700,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const projectTdStyle = {
  padding: "6px 4px",
  border: "1px solid #eee",
  textAlign: "center",
};

const projectInputStyle = {
  width: "100%",
  minWidth: 56,
  padding: "6px 4px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
  textAlign: "center",
  boxSizing: "border-box",
};

const tableLeftTitleRow = {
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #000",
};

const tableLeftTitle = {
  padding: "4px 8px",
  borderLeft: "1px solid #000",
  backgroundColor: "#f5f5f5",
  minWidth: 80,
};

const tableLeftValue = {
  padding: "4px 8px",
  minWidth: 90,
};

const customerHeader = {
  backgroundColor: "#000",
  color: "#fff",
  padding: "8px 14px",
  display: "inline-block",
  fontSize: 12,
};

const customerDetails = {
  fontSize: 12,
  marginTop: 6,
  lineHeight: 1.4,
};

const invoiceTable = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
  marginTop: 30,
  marginBottom: 40,
};

const invoiceTh = {
  border: "1px solid #000",
  padding: "6px 4px",
  backgroundColor: "#000",
  color: "#fff",
  verticalAlign: "middle",
};

const invoiceTd = {
  border: "1px solid #000",
  padding: "6px 4px",
  textAlign: "center",
  verticalAlign: "middle",
};

const invoiceTdCenter = {
  border: "1px solid #000",
  padding: 8,
  textAlign: "center",
  color: "#555",
  verticalAlign: "middle",
};

const subtotalTable = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 10,
  marginBottom: 50,
  fontSize: 12,
};

const subtotalTitle = {
  border: "1px solid #000",
  padding: "6px 4px",
  backgroundColor: "#000",
  color: "#fff",
  textAlign: "right",
  verticalAlign: "middle",
};

const subtotalValue = {
  border: "1px solid #000",
  padding: "6px 4px",
  textAlign: "center",
  width: "170px",
  verticalAlign: "middle",
};
const totalsTable = {
  width: "600px",
  borderCollapse: "collapse",
  marginTop: 20,
  marginBottom: 60,
  marginRight: "auto",
  marginLeft: "20px",
  fontSize: 12,
};

const totalsLabel = {
  border: "1px solid #999",
  padding: "8px",
  textAlign: "right",
  backgroundColor: "#f3f3f3",
  width: "65%",
  verticalAlign: "middle",
};

const totalsValue = {
  border: "1px solid #999",
  padding: "8px",
  textAlign: "center",
  width: "35%",
  verticalAlign: "middle",
};

const totalsRemaining = {
  border: "1px solid #000",
  padding: "8px",
  textAlign: "center",
  backgroundColor: "#000",
  color: "#fff",
  fontWeight: "bold",
  verticalAlign: "middle",
};

const notesBox = {
  border: "1px solid #999",
  marginTop: 10,
  marginBottom: 60,
  fontSize: 12,
  direction: "rtl",
};

const notesBoxTitle = {
  backgroundColor: "#000",
  color: "#fff",
  padding: "6px 10px",
  fontWeight: "bold",
};

const notesBoxBody = {
  padding: "10px 14px",
  lineHeight: 1.7,
  color: "#333",
};

const notesList = {
  margin: "0 0 4px",
  paddingRight: 18,
  paddingLeft: 0,
};

const notesSectionLabel = {
  fontWeight: "bold",
  marginBottom: 4,
};

const notesListItem = {
  marginBottom: 4,
};

/* =============================
     معاينة الفاتورة (الصورة اللي تنحفظ ويا كل نسخة)
     ============================= */
// مستخرجة من InvoicePage كما هي حرفياً حتى تُستخدم أيضاً لتوليد صورة فاتورة
// "طلب جديد" مال ارت تاتش (utils/generateInvoiceAssets.jsx) بنفس الشكل بالضبط.
export const InvoicePreview = React.forwardRef(({ invoice }, ref) => (
  <div
    ref={ref}
    style={{
      width: "1200px",
      minHeight: "600px",
      backgroundColor: "#ffffff",
      boxShadow: "0 0 8px rgba(0,0,0,0.15)",
padding: "40px 40px 170px 40px",
     direction: "rtl",
      fontFamily: "Tahoma, Arial, sans-serif",
      position: "relative",
    }}
  >
    {/* LOGO + TITLE */}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 30,
      }}
    >
     <div>
  <img
    src="/logo.png"   // 👈 هنا تحط صورة اللوكو
    alt="Artika Logo"
style={{
  width: 100,
  height: "auto",
  objectFit: "contain",
}}
  />
</div>
      <div style={{ textAlign: "right" }}>
        <h2 style={{ margin: 0 }}>أرتيكا</h2>
      </div>
    </div>
    {/* LEFT SMALL TABLE – DATE / NUMBER */}
    <div
      style={{
        position: "absolute",
        top: 80,
        left: 40,
        border: "1px solid #000",
        fontSize: 12,
      }}
    >
      <div style={tableLeftTitleRow}>
        <div style={tableLeftTitle}>التاريخ</div>
        <div style={tableLeftValue}>{invoice.date}</div>
      </div>

      <div style={tableLeftTitleRow}>
        <div style={tableLeftTitle}>رقم الفاتورة</div>
        <div style={tableLeftValue}>{invoice.invoiceNumber}</div>
      </div>

      <div style={tableLeftTitleRow}>
      </div>
    </div>

    {/* CUSTOMER INFO BLOCK */}
    <div
      style={{
        textAlign: "right",
        marginBottom: 25,
        marginTop: 40,
      }}
    >
      <div style={customerHeader}>معلومات الزبون</div>

      <div style={customerDetails}>
        <div>اسم الزبون: {invoice.customerName || "-"}</div>
          <div>رقم الهاتف: {invoice.phone || "-"}</div>
        {invoice.projectName && (
          <div>المشروع: {invoice.projectName}</div>
        )}
        {invoice.companyName && (
<div>الشركة: {invoice.companyName}</div>
)}
        <div>
          العنوان: {invoice.governorate || "-"}{" "}
          {invoice.nearestPoint && `، ${invoice.nearestPoint}`}
        </div>
      </div>
    </div>

    {/* ITEMS TABLE */}
    <table style={invoiceTable}>
   <thead>
  <tr>
    <th style={invoiceTh}>السعر الكلي (دينار عراقي)</th>
    <th style={invoiceTh}>سعر اللوحة (دينار عراقي)</th>
    <th style={invoiceTh}>العدد</th>
    <th style={invoiceTh}>الأبعاد</th>
    <th style={invoiceTh}>شكل اللوحة</th>
    <th style={invoiceTh}>الإطار</th>
    <th style={invoiceTh}>الوصف</th>
  </tr>
</thead>

      <tbody>
        {invoice.items.length === 0 && (
          <tr>
            <td colSpan={7} style={invoiceTdCenter}>
              لا توجد لوحات بعد
            </td>
          </tr>
        )}

        {invoice.items.map((item, i) => (
          <tr key={i}>
            <td style={invoiceTd}>
              {item.total.toLocaleString()}
            </td>
            <td style={invoiceTd}>
              {item.pricePerPanel.toLocaleString()}
            </td>
            <td style={invoiceTd}>{item.quantity}</td>
            <td style={invoiceTd}>
              {item.sizeKey.replace("x", "×")}
            </td>
            <td style={invoiceTd}>
  {item.panelShape || "-"}
</td>
<td style={invoiceTd}>
  {isWallpaper(item) ? (
    wallpaperInstallLabel(item)
  ) : item.frameOption === "إطار كامل" && item.frameType && item.frameColor ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
      <span>
       {item.frameType === "أخرى" ? item.customFrameType : item.frameType} - {item.frameColor === "أخرى" ? item.customFrameColor : item.frameColor}
      </span>
      {FRAME_IMAGES[item.frameType]?.[item.frameColor] && (
<img
  src={FRAME_IMAGES[item.frameType][item.frameColor]}
  alt="frame"
  style={{ width: 30, height: 30, objectFit: "cover", borderRadius: 4 }}
/>
      )}
    </div>
  ) : (
    item.frameOption
  )}
</td>                   <td
              style={{
                ...invoiceTd,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                direction: "rtl",
              }}
            >
              {item.panelName} – {resolvePanelTypeLabel(item.panelType, invoice.panelTypeLabels)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>

{/* SUBTOTAL */}
<table style={subtotalTable}>
  <tbody>
    <tr>
      <td style={subtotalValue}>
{invoice.subtotal.toLocaleString()}
      </td>
      <td style={subtotalTitle}>
المجموع الأولي (دينار عراقي)
      </td>
    </tr>
    {invoice.partnerDiscountAmount > 0 && (
      <>
        <tr>
          <td style={subtotalValue}>
{invoice.partnerDiscountAmount.toLocaleString()}
          </td>
          <td style={subtotalTitle}>
خصم {invoice.partnerDiscountPercent}% على اللوحات
          </td>
        </tr>
        <tr>
          <td style={subtotalValue}>
{(invoice.subtotal - invoice.partnerDiscountAmount).toLocaleString()}
          </td>
          <td style={subtotalTitle}>
المجموع بعد الخصم (دينار عراقي)
          </td>
        </tr>
      </>
    )}
  </tbody>
</table>

{/* TOTALS TABLE */}
<table style={totalsTable}>
  <tbody>

  {invoice.consultationFee > 0 && (
  <tr>
    <td style={totalsValue}>{invoice.consultationFee.toLocaleString()}</td>
    <td style={totalsLabel}>رسوم التصميم والاستشارة الهندسية</td>
  </tr>
)}

    {invoice.transportFee > 0 && !invoice.hideTransportFeeFromCustomer && (
  <tr>
    <td style={totalsValue}>{invoice.transportFee.toLocaleString()}</td>
    <td style={totalsLabel}>رسوم التوصيل</td>
  </tr>
)}

{invoice.installationFee > 0 && (
  <tr>
    <td style={totalsValue}>{invoice.installationFee.toLocaleString()}</td>
    <td style={totalsLabel}>رسوم التركيب</td>
  </tr>
)}
{invoice.siteVisitFee > 0 && (
  <tr>
    <td style={totalsValue}>{invoice.siteVisitFee.toLocaleString()}</td>
    <td style={totalsLabel}>زيارة الموقع</td>
  </tr>
)}

   {invoice.discount > 0 && (
<tr>
  <td style={totalsValue}>{invoice.discount.toLocaleString()}</td>
  <td style={totalsLabel}>التخفيض</td>
</tr>
)}

    <tr>
      <td style={totalsValue}>{invoice.finalTotal.toLocaleString()}</td>
      <td style={totalsLabel}>المبلغ الكلي (دينار عراقي)</td>
    </tr>

    {invoice.advancePayment > 0 && (
      <>
<tr>
  <td style={totalsValue}>{invoice.advancePayment.toLocaleString()}</td>
  <td style={totalsLabel}>الدفعة المقدمة</td>
</tr>

<tr>
  <td style={totalsRemaining}>{invoice.remainingPayment.toLocaleString()}</td>
  <td style={totalsLabel}>المبلغ المتبقي (دينار عراقي)</td>
</tr>
      </>
    )}

  </tbody>
</table>

{/* NOTES BOX (رسالة السعر التلقائية + ملاحظات الموظفة) */}
{(() => {
  const showInstallationNote =
    invoice.items.length > 0 && invoice.installationFee === 0;

  const employeeNoteLines = (invoice.notes || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!showInstallationNote && employeeNoteLines.length === 0) return null;

  return (
    <div style={notesBox}>
      <div style={notesBoxTitle}>ملاحظات</div>
      <div style={notesBoxBody}>
{showInstallationNote && (
  <div style={notesListItem}>السعر غير شامل خدمة التركيب.</div>
)}
{employeeNoteLines.length > 0 && (
  <>
    <div
      style={{
        ...notesSectionLabel,
        marginTop: showInstallationNote ? 10 : 0,
      }}
    >
      ملاحظات إضافية:
    </div>
    <ul style={notesList}>
      {employeeNoteLines.map((line, idx) => (
        <li key={idx} style={notesListItem}>{line}</li>
      ))}
    </ul>
  </>
)}
      </div>
    </div>
  );
})()}

{/* ©️ حقوق التصاميم (utils/copyrightNotice.js) - جزء من فوتر الفاتورة: خط
    رفيع + نص رصاصي صغير بنفس خط/لون معلومات التواصل، فوكها مباشرة */}
<div style={copyrightFooter}>
  <span style={copyrightTitle}>© {COPYRIGHT_TITLE}</span> - {COPYRIGHT_TEXT}
  <div style={copyrightEn}>{copyrightLineEn(copyrightYear(invoice.date))}</div>
</div>

{/* FOOTER (ثابت بكل فاتورة) */}
<div
  style={{
    position: "absolute",
    left: 40,
    right: 40,
bottom: 30,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    fontSize: 11,
    color: "#888",
  }}
>
  {/* Artika.ai يصير يسار */}
  <div style={{ fontWeight: 700, color: "#aaa" }}>Artika.ai</div>

  {/* المعلومات تصير يمين */}
  <div style={{ lineHeight: 1.5, textAlign: "right" }}>
    <div>Email : {FOOTER_INFO.email}</div>
    <div>Phone : {FOOTER_INFO.phone}</div>
    <div>Website : {FOOTER_INFO.website}</div>
    <div>{FOOTER_INFO.location}</div>
  </div>
</div>
  </div>
));

// ©️ حقوق التصاميم بفوتر الفاتورة - نفس حجم ولون نص الفوتر (11px رصاصي)،
// فوك معلومات التواصل (الفوتر bottom: 30 وطوله ~65px) ومفصول بخط رفيع
const copyrightFooter = {
  position: "absolute",
  left: 40,
  right: 40,
  bottom: 104,
  paddingTop: 8,
  borderTop: "1px solid #ddd",
  fontSize: 11,
  lineHeight: 1.7,
  color: "#888",
  textAlign: "center",
  direction: "rtl",
};
const copyrightTitle = { fontWeight: 700, color: "#666" };
const copyrightEn = { fontSize: 10, color: "#aaa", direction: "ltr", marginTop: 2 };

// يُستخدمان أيضاً بتوليد ملف PDF فاتورة ارت تاتش
export { buildMultiPagePdf, urlToDataUrl };

export default InvoicePage;
